// Builds an Economic Process Modelling (EPM) scorecard from the analysis graph.
//
// The scorecard follows the EPM hybrid layout: hard numbers (revenue, cost, friction) in an Economic P&L,
// forward looking items (risk, option value, information value) on an Economic Balance Sheet in their own
// units, a Value vs Drag ledger, drill-in lenses and prioritised decisions.
//
// Every figure is derived from the model. Figures read directly from modelled data are "grounded"; figures
// inferred from the shape of the graph are "indicative". Nothing is invented: where the model has no data
// (for example an unpriced role, or a role with no people) the scorecard says so.
import { buildInformationRisks, EpmInformationRiskRow, EpmRekeyRow } from './information-risk';
import { buildValueProfile, ValueProfileBar } from './value-profile';
import { systemKindOf } from '../common/system-kind';
import { derivedValueOutsideSystems, heldOutsideSystems, INFORMATION_HELD_LABELS, informationHeld } from '../common/information-value';
import { EDGE_TYPE, FREQUENCY_TYPE, getHoursPerYear, GraphData, NODE_SUPERTYPE, NODE_TYPE, NodeInsights, ORGANISATION_MODEL_KEY, SYSTEM_KIND, WORK_TYPE } from '../common/linqset-model';
import { AnalysisEdge, AnalysisNode, isUseOf, nextSteps } from '../common/analysis-model';
import { ACTION_TYPE } from '../common/organisation';
import { AIEpmDecision, EPM_EVIDENCE_LIMITS, EpmDecisionEvidence, EpmLever, EpmNodeKind } from '../common/epm-decisions';
import { AssetInput, buildInformationAnalysis, InformationAssetSummary } from '../information/information-analysis-model';

export type Grounding = 'grounded' | 'indicative';

// the heatmap dimension chosen on the analysis page, as the scorecard shows it
export interface DimensionSummary {
    dimension: string;
    total?: string;          // only for dimensions that add up across steps, such as cost or time
    steps: { key: string; name: string; label: string; share: number; colour: string }[];   // the highest first; share is of the heatmap's scale
}

// a role or system from the organisation model, with its organisation-wide insights
export interface EpmComponent {
    _key: string;
    name: string;
    superType: NODE_SUPERTYPE;
    type: NODE_TYPE;
    people: number;                     // people set on the role (0 when capacity isn't modelled)
    hasPeople?: boolean;                // roles: people are set on it, or attached to it
    capacitySet?: boolean;              // roles: its capacity is set on the role itself
    systemKind?: SYSTEM_KIND;           // systems: system of record, business system or local tool
    systemKindConfirmed?: boolean;      // said by the user, rather than LINQ's read of its name
    insights?: NodeInsights;            // organisation-wide insights for the component
    modelInsights: NodeInsights[];      // insights for each model it appears in
}

// roles and systems from the organisation model, with their organisation-wide and per-model insights for the workspace
export const epmComponents = (organisationModel: GraphData, workspaceKey: string): EpmComponent[] =>
    organisationModel.nodes
        .filter((n) => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM)
        .map((n) => {
            const insights = (n.insights || []).filter((i) => i.projectKey === workspaceKey);
            return {
                _key: n._key,
                name: n.name,
                superType: n.superType,
                type: n.type,
                people: n.properties?.people || 0,
                ...(n.superType === NODE_SUPERTYPE.ROLE ? {
                    hasPeople: (n.properties?.people || 0) > 0 || !!insights.find((i) => i.modelKey === ORGANISATION_MODEL_KEY)?.hasPeople,
                    // as the calculation reads it: hours set, rather than left to be worked out from the work
                    capacitySet: !!n.properties?.frequency && n.properties.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED && getHoursPerYear(n) > 0,
                } : {}),
                ...(n.superType === NODE_SUPERTYPE.SYSTEM ? (({ kind, confirmed }) => ({ systemKind: kind, systemKindConfirmed: confirmed }))(systemKindOf(n)) : {}),
                insights: insights.find((i) => i.modelKey === ORGANISATION_MODEL_KEY),
                modelInsights: insights.filter((i) => i.modelKey !== ORGANISATION_MODEL_KEY),
            };
        });

export interface EpmScorecardInput {
    scopeName: string;                  // e.g. "Organisation (4 models)" or a model name
    workspaceName: string;
    modelKeys: string[];
    nodes: AnalysisNode[];              // actions, outputs and outcomes in the graph
    edges: AnalysisEdge[];
    components: EpmComponent[];         // roles and systems
    modelNames: { [modelKey: string]: string };
    // revenue and value totals from the model insights. Outputs also carry revenue passed back from other
    // models through shared outputs, so adding up the outputs would count it twice
    totals: { revenue: number; value: number };
    // each model's revenue and value, without what it receives from the other models shown (counted in theirs), so they
    // add up to the totals. For the value profile, drawn when more than one model is shown
    modelValues?: { [modelKey: string]: { revenue: number; value: number } };
    defaultCapacity?: boolean;          // the organisation has a default capacity, which roles without their own take
    assets?: AssetInput[];              // the organisation's information assets, with their insights in what's scored
    currency: string;                   // currency code, for the AI evidence
    money: (value: number) => string;
    number: (value: number) => string;
}

// the number behind a figure, so the same figure in another state (current or future) can be compared with it
export interface EpmMeasure {
    amount: number;
    better: 'up' | 'down';
    unit: 'money' | 'hours' | 'percent' | 'count' | 'number' | 'time';     // time: hours, shown as minutes, hours or days
}

export interface EpmHeadline {
    label: string;
    value: string;
    detail: string;
    tone: 'good' | 'bad' | 'warn' | 'neutral';
    grounding: Grounding;
    measure?: EpmMeasure;
}

// a row of a panel. Where a panel breaks a figure down, the row says which part of that it is: a group, with what it
// adds up to; an item within the group above it; or a total. A row without one is a figure of its own
export interface EpmLine {
    label: string;
    value: string;
    kind?: 'group' | 'item' | 'total';
    measure?: EpmMeasure;
}

const measure = (amount: number, better: EpmMeasure['better'], unit: EpmMeasure['unit']): EpmMeasure => ({ amount, better, unit });

export interface EpmPanel {
    title: string;
    sub: string;
    grounding: Grounding;
    unit: 'money' | 'index' | 'count' | 'score';
    lines: EpmLine[];
    note: string;
}

export interface EpmBottleneck {
    roleName: string;
    load: number;               // percentage
    demandHours: number;
    capacityHours: number;
    people: number;
    shortfallHours: number;
    shortfallCost: number;
    text: string;
    overloaded: boolean;
}

// a detour from the main flow: items leave it at one point and return at another, and the work between is exception
// work. Marked work with no detour is a row of its own, with nowhere it leaves or returns
export interface EpmDetourRow {
    key: string;                // "<split>><join>", or the action's key for work marked by the user
    modelKey: string;
    stepKeys: string[];         // the actions on it, to correct whether they are exception work
    leavesAt: string;
    returnsAt: string;
    model: string;
    steps: string[];
    share: number;              // percent of the items leaving the split that take the detour
    items: string;
    hours: string;
    cost: string;
    confirmed: boolean;         // the user marked the work as exception work
}

// a step said not to add value: waste other than exception work (re-keying, duplicate checks, moving information)
export interface EpmWasteRow {
    key: string;                // the action
    modelKey: string;
    name: string;
    model: string;
    items: string;
    hours: string;
    cost: string;
}

// a step where items wait (see WORK_TYPE): nobody works on it, so it costs nothing, but it delays the items
export interface EpmWaitRow {
    key: string;                // the action
    modelKey: string;
    name: string;
    model: string;
    waitEach: number;           // hours each item waits there
    items: number;              // a year
    waitHours: number;          // hours of waiting a year, over all its items
    perItemIn: number;          // its share of the time per item entering the process, in hours
    confirmed: boolean;         // the user said items wait there, rather than LINQ reading it
}

export interface EpmFrictionRow {
    key: string;                // the output, to show its context
    modelKey: string;
    name: string;
    model: string;
    state: 'Dead end' | 'Unvalued';
    items: string;
    hours: string;
    cost: string;
}

export interface EpmRiskRow {
    key: string;                // the role or system, to show its context
    name: string;
    kind: 'Role' | 'System';
    why: string;
    dependency: string;
    load: string;
    revenueAtRisk: string;
    revenueCreated: string;     // its share of the revenue its work creates, in the models shown
}

// an output's value is set by users (per item), or derived by LINQ from how the output is used when no one has set one
export type EpmInformationState = 'Revenue bearing' | 'Valued' | 'In use' | 'Dead end';

export interface EpmInformationRow {
    key: string;                // the output node, so a suggested value can be applied to it
    joinedOut?: boolean;        // joined into another model, which its value comes from, so none is set on it
    modelKey: string;
    name: string;
    model: string;
    value: string;              // value a year from the values users set
    userValue: number;          // value per item the user set, 0 when none
    derivedValue: number;       // value per item derived from use, 0 to 100 across the organisation
    derived: string;
    type?: NODE_TYPE;           // the type it is held as is read from, to correct
    typeRead?: NODE_TYPE;       // LINQ's read of the type, where it's typed as plain information, to confirm
    held: string;               // how it is held, from its type: in a system, as a file, on paper, by word of mouth
    heldOutside: boolean;
    revenue: string;
    reuse: string;
    costToProduce: string;
    state: EpmInformationState;
    asset?: string;             // the information asset it is a state of, if any
}

export type { EpmLever } from '../common/epm-decisions';

// an item a data gap is about, to open in the model it's in
export interface EpmNodeLink {
    key: string;
    name: string;
    modelKey: string;
    model?: string;             // the model's name, for items in a model
}

export interface EpmDataGap {
    text: string;
    items: EpmNodeLink[];       // by name
}

export interface EpmNodeRef {
    kind: EpmNodeKind;
    name: string;
}

export interface EpmDecision {
    sequence: number;           // 0 is the gate
    gate: boolean;
    lever: EpmLever;
    title: string;
    signal: string;
    rationale: string;
    nodes: EpmNodeRef[];
    impacts: { dimension: string; movement: string }[];
    effort: 'Low' | 'Medium' | 'High';
    confidence: 'Low' | 'Medium' | 'High' | 'n/a';
    dependsOn: string;
    priority: number;           // 0 to 100
    grounding: Grounding;
    subjects: EpmSubject[];     // the signals the decision addresses, used to check it in a future state
    badge?: string;             // the gate's label when there is more than one gate (G1, G2)
}

// a signal a decision addresses: an overloaded role, a friction output, heavy manual work, a single point of failure,
// or unvalued information. The key is the node key (or 'all' for unvalued information)
export interface EpmSubject {
    kind: keyof EpmSignals;
    key: string;
}

export interface EpmSignals {
    overloaded: string[];
    friction: string[];
    manual: string[];
    singlePoints: string[];
    unvalued: string[];
    detours: string[];          // the keys of the detour rows
    bridges: string[];          // manual bridges between systems
    copies: string[];           // unofficial copies of information
    localTools: string[];       // important information in local tools
    rekeying: string[];         // steps re-keying paper or spoken information
    asset: string[];            // information assets in use that no one has valued
    assetHeld: string[];        // valuable information assets with a state kept outside a controlled system
    assetRisk: string[];        // information assets their register or holding puts at risk
    waits: string[];            // steps where items wait
}

export type EpmDecisionStatus = 'resolved' | 'improved' | 'open' | 'untracked';

export interface EpmScorecard {
    scopeName: string;
    workspaceName: string;
    summary: string;
    headlines: EpmHeadline[];
    bottleneck?: EpmBottleneck;
    profitAndLoss: EpmPanel[];
    balanceSheet: EpmPanel[];
    ledger: { value: EpmLine[]; drag: EpmLine[] };
    frictionRows: EpmFrictionRow[];
    detourRows: EpmDetourRow[];
    wasteRows: EpmWasteRow[];                   // steps said not to add value: friction
    waitRows: EpmWaitRow[];                     // steps where items wait, the most waiting first
    frictionNote: string;
    riskRows: EpmRiskRow[];
    riskNote: string;
    informationRisks: EpmInformationRiskRow[];  // information leaving the systems that should hold it
    rekeyRows: EpmRekeyRow[];                   // paper and spoken information typed into systems: friction
    informationRiskNote: string;
    informationRows: EpmInformationRow[];
    informationNote: string;
    decisions: EpmDecision[];
    dataGaps: string[];
    dataGapDetails: EpmDataGap[];       // the same, with the items each is about
    evidence: EpmDecisionEvidence;      // what AI decisions are generated from
    signals: EpmSignals;
    valueProfile: ValueProfileBar[];    // what each model shown contributes, when there is more than one
}

// ---------- analysis helpers ----------

const isAction = (n: AnalysisNode) => n.superType === NODE_SUPERTYPE.ACTION;
const isOutput = (n: AnalysisNode) => n.superType === NODE_SUPERTYPE.INFORMATION;
const isRole = (c: EpmComponent) => c.superType === NODE_SUPERTYPE.ROLE && c.type !== NODE_TYPE.EXTERNAL_ORGANISATION;
const isSystem = (c: EpmComponent) => c.superType === NODE_SUPERTYPE.SYSTEM;

// a role's people: set on it, or attached to it
const hasPeople = (c: EpmComponent) => c.hasPeople ?? c.people > 0;
// the people a role's capacity is worked out for: those set on it or attached to it, or, with none, the one person LINQ
// takes it to have
const capacityPeople = (c: EpmComponent) => c.people || c.insights?.numPeople || 1;

// capacity is modelled when the role has people, its capacity is set on it, or the organisation has a default capacity
// it takes (a full-time year for each person); otherwise LINQ makes it match the work, which says nothing about load
export const hasModelledCapacity = (c: EpmComponent, defaultCapacity = false) =>
    (hasPeople(c) || !!c.capacitySet || defaultCapacity) && (c.insights?.modelledCapacity || 0) > 0;

export const roleLoad = (c: EpmComponent, defaultCapacity = false) =>
    hasModelledCapacity(c, defaultCapacity) ? Math.round((100 * (c.insights?.hours || 0)) / c.insights!.modelledCapacity) : undefined;

// manual actions are performed by roles only; manual work fails more often, so it is the basis of the risk read
export type ActionClass = 'manual' | 'automated' | 'assisted' | 'unassigned' | 'external';
export const classifyAction = (n: AnalysisNode, externalRoleKeys: Set<string>): ActionClass => {
    // LINQ's own classification, where the analysis data carries it
    switch (n.actionType) {
        case ACTION_TYPE.MANUAL: return 'manual';
        case ACTION_TYPE.AUTOMATED: return 'automated';
        case ACTION_TYPE.BOTH: return 'assisted';
        case ACTION_TYPE.EXTERNAL: return 'external';
        case ACTION_TYPE.NONE: return 'unassigned';
    }
    // otherwise read it from the performers. A role's systems are those it uses anywhere, so this can overstate assisted work
    const roles = (n.roles || []).filter((r) => !externalRoleKeys.has(r._key));
    const systems = (n.systems || []).length + roles.reduce((t, r) => t + (r.systemKeys?.length || 0), 0);
    if (roles.length > 0 && systems === 0) return 'manual';
    if (roles.length === 0 && systems > 0) return 'automated';
    if (roles.length > 0) return 'assisted';
    return (n.roles || []).length > 0 ? 'assisted' : 'unassigned';
};

// how many downstream actions, outputs and outcomes consume an output (its reuse): the outcomes it realises only once
// a value or revenue is set on it
const downstreamCount = (o: AnalysisNode, edges: AnalysisEdge[]) =>
    new Set(edges.filter((e) => (e.from === o._key || e.informationKey === o._key) && isUseOf(e, o)).map((e) => e.to).filter((to) => to !== o._key)).size;

const hourlyCost = (c: EpmComponent) => {
    const hours = c.insights?.hours || 0;
    const cost = c.insights?.allocatedCostsByType?.['total'] || 0;
    return hours > 0 ? cost / hours : 0;
};

// ---------- scorecard ----------

// a node without a name still gets one people can read, wherever the scorecard names it (decisions, tables and the
// evidence AI is given): "Unnamed output (Orders)" rather than "undefined"
const hasName = (n: { name?: string }) => typeof n.name === 'string' && n.name.trim().length > 0;

export const nameUnnamedNodes = (input: EpmScorecardInput): Pick<EpmScorecardInput, 'nodes' | 'components'> => {
    const inModel = (key: string) => (input.modelNames[key] ? ` (${input.modelNames[key]})` : '');
    const kind = (n: AnalysisNode) => (isAction(n) ? 'action' : isOutput(n) ? 'output' : 'step');
    return {
        nodes: input.nodes.map((n) => (hasName(n) ? n : { ...n, name: `Unnamed ${kind(n)}${inModel(n.modelKey)}` })),
        components: input.components.map((c) => (hasName(c) ? c : { ...c, name: `Unnamed ${c.superType === NODE_SUPERTYPE.ROLE ? 'role' : 'system'}` })),
    };
};

export const buildEpmScorecard = (input: EpmScorecardInput): EpmScorecard => {
    const { edges, money, number } = input;
    const { nodes, components } = nameUnnamedNodes(input);
    const modelName = (key: string) => input.modelNames[key] || '';

    const actions = nodes.filter(isAction);
    const outputs = nodes.filter(isOutput);
    const externalRoleKeys = new Set(components.filter((c) => c.type === NODE_TYPE.EXTERNAL_ORGANISATION).map((c) => c._key));

    // components that take part in the graph shown
    const performerKeys = new Set<string>();
    actions.forEach((a) => {
        (a.roles || []).forEach((r) => {
            performerKeys.add(r._key);
            (r.systemKeys || []).forEach((s) => performerKeys.add(s));
        });
        (a.systems || []).forEach((s) => performerKeys.add(s));
    });
    const inScope = components.filter((c) => performerKeys.has(c._key));
    const roles = inScope.filter(isRole);
    const systems = inScope.filter(isSystem);

    // ----- P&L: revenue, cost, friction -----
    const { revenue, value } = input.totals;
    const revenueOutputs = outputs.filter((o) => o.revenue > 0).sort((a, b) => b.revenue - a.revenue);
    const creatingActions = actions.filter((a) => (a.contributedRevenue || 0) > 0).sort((a, b) => (b.contributedRevenue || 0) - (a.contributedRevenue || 0));
    // a group with what all its parts come to, its largest parts, and the rest in one row, so its items add up to it
    const breakdown = (label: string, noun: string, parts: { name: string; amount: number }[], shown: number): EpmLine[] => {
        if (!parts.length) return [];
        const sum = (ps: typeof parts) => ps.reduce((t, p) => t + p.amount, 0);
        const rest = parts.slice(shown);
        return [
            { label, value: money(sum(parts)), kind: 'group' },
            ...parts.slice(0, shown).map((p): EpmLine => ({ label: p.name, value: money(p.amount), kind: 'item' })),
            ...(rest.length ? [{ label: `${rest.length} other ${noun}${rest.length === 1 ? '' : 's'}`, value: money(sum(rest)), kind: 'item' as const }] : []),
        ];
    };

    const componentCost = (c: EpmComponent) =>
        c.modelInsights.filter((i) => input.modelKeys.includes(i.modelKey)).reduce((t, i) => t + (i.allocatedCostsByType?.['total'] || 0), 0);
    const roleCosts = roles.map((r) => ({ c: r, cost: componentCost(r) })).sort((a, b) => b.cost - a.cost);
    const systemCosts = systems.map((s) => ({ c: s, cost: componentCost(s) })).sort((a, b) => b.cost - a.cost);
    const peopleCost = roleCosts.reduce((t, r) => t + r.cost, 0);
    const systemCost = systemCosts.reduce((t, s) => t + s.cost, 0);
    const cost = peopleCost + systemCost;
    const net = revenue - cost;

    // an output is in use when anything consumes it: LINQ counts its consumers across every model (an output can be
    // used in a model outside the scope shown), and the graph shown is read as well for analysis data without the count
    const inUse = (o: AnalysisNode) => (o.consumers || 0) > 0 || downstreamCount(o, edges) > 0;
    const deadEnd = (o: AnalysisNode) => !inUse(o);
    // outputs no value or revenue flows to. Those in use carry a value derived from how they are used, so friction is
    // only the dead ends: the work behind outputs nothing uses (the LINQ definition, see information-value.ts)
    const zeroValueOutputs = outputs.filter((o) => !o.value && !o.revenue);
    const frictionOutputs = zeroValueOutputs.filter(deadEnd).filter((o) => (o.totalHours || 0) > 0 || (o.totalCost || 0) > 0);
    // outputs in use that no one has scored: their worth rests on the derived value until someone confirms it
    const unscored = outputs.filter((o) => !o.userValue && !o.revenue && !o.joinedOut && inUse(o));
    const deadEndCost = frictionOutputs.reduce((t, o) => t + (o.totalCost || 0), 0);
    const deadEndHours = frictionOutputs.reduce((t, o) => t + (o.totalHours || 0), 0);

    // exception work: actions on a detour from the main flow (items leave it and return later), or marked as exception
    // work by the user. Grouped by where the items leave and return, so each detour reads as one piece of work
    const exceptionActions = actions.filter((a) => a.exceptionWork);
    const exceptionCost = exceptionActions.reduce((t, a) => t + (a.cost || 0), 0);
    const exceptionHours = exceptionActions.reduce((t, a) => t + (a.hours || 0), 0);
    // information and system risk: information leaving the systems that should hold it, and the re-keying of paper and
    // spoken information into systems, which is friction too
    const infoRisk = buildInformationRisks({
        actions, outputs, edges, externalRoleKeys, modelName, number, money,
        systems: components.filter(isSystem).map((c) => ({ key: c._key, name: c.name, kind: c.systemKind || SYSTEM_KIND.BUSINESS, confirmed: !!c.systemKindConfirmed })),
    });
    // other waste: steps said not to add value. A step that re-keys, or is on a manual bridge, is counted there instead,
    // as that says why it adds nothing (see information-risk.ts)
    const wasteActions = actions.filter((a) => !a.exceptionWork && a.workKind === WORK_TYPE.NOT_VALUE_ADDING && !infoRisk.claimedWasteKeys.has(a._key))
        .sort((a, b) => (b.cost || 0) - (a.cost || 0) || (b.hours || 0) - (a.hours || 0));
    const wasteCost = wasteActions.reduce((t, a) => t + (a.cost || 0), 0);
    const wasteHours = wasteActions.reduce((t, a) => t + (a.hours || 0), 0);
    const wasteRows: EpmWasteRow[] = wasteActions.map((a) => ({
        key: a._key, modelKey: a.modelKey, name: a.name, model: modelName(a.modelKey),
        items: number(a.items || 0), hours: number(a.hours || 0), cost: money(a.cost || 0),
    }));
    const { rekeying, rekeyHours, rekeyCost, bridgeFrictionHours, bridgeFrictionCost } = infoRisk;
    // the manual bridges whose steps take staff time: friction as well as a risk
    const bridgeFriction = infoRisk.bridges.filter((r) => r.alsoFriction);
    const nodeNames = new Map<string, string>(nodes.map((n) => [n._key, n.name]));
    edges.forEach((e) => { if (e.informationKey && e.informationName && !nodeNames.has(e.informationKey)) nodeNames.set(e.informationKey, e.informationName); });
    const detourGroups = new Map<string, AnalysisNode[]>();
    // steps the user marked as exception work, where LINQ found no detour (as where the work returns at an output
    // rather than at a step): those joined one to the next are one piece of exception work, in the order work passes
    // through them
    const marked = exceptionActions.filter((a) => !(a.onDetour && a.detourSplitKey && a.detourJoinKey));
    const markedKeys = new Set(marked.map((a) => a._key));
    const within = edges.filter((e) => markedKeys.has(e.from) && markedKeys.has(e.to) && e.from !== e.to);
    const placed = new Set<string>();
    const markedRoute = new Map<string, { into: AnalysisEdge[]; out: AnalysisEdge[] }>();
    marked.forEach((a) => {
        if (placed.has(a._key)) return;
        const part = new Set([a._key]);
        for (const queue = [a._key]; queue.length;) {
            const at = queue.shift()!;
            within.filter((e) => e.from === at || e.to === at).map((e) => (e.from === at ? e.to : e.from))
                .filter((k) => !part.has(k)).forEach((k) => { part.add(k); queue.push(k); });
        }
        // from where work comes in to it, along the steps that follow
        const ordered: string[] = [];
        const heads = [...part].filter((k) => !within.some((e) => e.to === k && part.has(e.from)));
        for (const queue = heads.length ? heads : [a._key]; queue.length;) {
            const at = queue.shift()!;
            if (ordered.includes(at)) continue;
            ordered.push(at);
            queue.push(...within.filter((e) => e.from === at && part.has(e.to)).map((e) => e.to));
        }
        [...part].filter((k) => !ordered.includes(k)).forEach((k) => ordered.push(k));
        ordered.forEach((k) => placed.add(k));
        const steps = ordered.map((k) => marked.find((m) => m._key === k)!);
        detourGroups.set(ordered[0], steps);
        markedRoute.set(ordered[0], {
            into: edges.filter((e) => part.has(e.to) && !part.has(e.from)),
            out: edges.filter((e) => part.has(e.from) && !part.has(e.to)),
        });
    });
    exceptionActions.filter((a) => !markedKeys.has(a._key)).forEach((a) => {
        const key = `${a.detourSplitKey}>${a.detourJoinKey}`;
        (detourGroups.get(key) || detourGroups.set(key, []).get(key)!).push(a);
    });
    const namesOf = (keys: string[]) => [...new Set(keys.map((k) => nodeNames.get(k)).filter((n): n is string => !!n))].join(', ');
    const detourRows: EpmDetourRow[] = [...detourGroups.entries()].map(([key, steps]) => {
        const lead = steps[0];
        const onDetour = key.includes('>');
        const route = markedRoute.get(key);
        // for marked steps: the share of what leaves the step before them that comes their way
        const from = route ? [...new Set(route.into.map((e) => e.from))] : [];
        // by the percentage sent along each connection: one into an output carries all the output's items, from
        // every step making it, so its items overstate what this step sends
        const leaving = edges.filter((e) => from.includes(e.from)).reduce((t, e) => t + (e.percentage || 0), 0);
        const markedShare = leaving ? route!.into.reduce((t, e) => t + (e.percentage || 0), 0) / leaving : 0;
        // only some of it comes their way: they're off the main flow, leaving it and returning to it; steps every item
        // passes through are on it
        const offFlow = markedShare > 0 && markedShare < 1;
        const items = Math.max(...steps.map((a) => a.items || 0), 0);
        return {
            key,
            modelKey: lead.modelKey,
            stepKeys: steps.map((a) => a._key),
            leavesAt: onDetour ? nodeNames.get(lead.detourSplitKey!) || 'an earlier step' : (offFlow && namesOf(from)) || '-',
            returnsAt: onDetour ? nodeNames.get(lead.detourJoinKey!) || 'a later step' : (offFlow && namesOf(route!.out.map((e) => e.to))) || '-',
            model: modelName(lead.modelKey),
            steps: steps.map((a) => a.name),
            share: Math.round(100 * (onDetour ? Math.max(...steps.map((a) => a.detourShare || 0)) : offFlow ? markedShare : 0)),
            items: number(items),
            hours: number(steps.reduce((t, a) => t + (a.hours || 0), 0)),
            cost: money(steps.reduce((t, a) => t + (a.cost || 0), 0)),
            confirmed: steps.every((a) => a.workType === WORK_TYPE.EXCEPTION),
        };
    }).sort((a, b) => parseFloat(b.cost.replace(/[^0-9.]/g, '')) - parseFloat(a.cost.replace(/[^0-9.]/g, '')) || b.steps.length - a.steps.length);
    const detourCost = (row: EpmDetourRow) => detourGroups.get(row.key)!.reduce((t, a) => t + (a.cost || 0), 0);
    const detourHours = (row: EpmDetourRow) => detourGroups.get(row.key)!.reduce((t, a) => t + (a.hours || 0), 0);
    detourRows.sort((a, b) => detourCost(b) - detourCost(a) || detourHours(b) - detourHours(a));

    // friction is the dead ends, the exception work, the other waste, the re-keying and the manual bridges together, each
    // step's time counted once
    const frictionCost = deadEndCost + exceptionCost + wasteCost + rekeyCost + bridgeFrictionCost;
    const frictionHours = deadEndHours + exceptionHours + wasteHours + rekeyHours + bridgeFrictionHours;
    // friction is shown in money, or in hours when the time on it isn't priced (no friction at all reads as $0)
    const frictionInMoney = frictionCost > 0 || frictionHours === 0;
    const totalHours = actions.reduce((t, a) => t + (a.hours || 0), 0);

    // ----- flow: the time each item takes, working and waiting -----
    // Each step's hours (and waiting hours) are its items times its duration, so adding them up over the steps and
    // dividing by the items entering the process gives the time an item spends on its way, on average. Items enter where
    // a step sets how often it happens. Steps done side by side both count, so it can be more than start to finish
    const itemsIn = actions.filter((a) => a.setsFrequency).reduce((t, a) => t + (a.items || 0), 0);
    const waitHours = actions.reduce((t, a) => t + (a.waitHours || 0), 0);
    const workPerItem = itemsIn > 0 ? totalHours / itemsIn : 0;
    const waitPerItem = itemsIn > 0 ? waitHours / itemsIn : 0;
    const timePerItem = workPerItem + waitPerItem;
    const flowEfficiency = timePerItem > 0 ? Math.round((100 * workPerItem) / timePerItem) : 100;
    const waitRows: EpmWaitRow[] = actions
        .filter((a) => a.workKind === WORK_TYPE.WAITING && (a.waitHours || 0) > 0)
        .sort((a, b) => (b.waitHours || 0) - (a.waitHours || 0))
        .map((a) => ({
            key: a._key, modelKey: a.modelKey, name: a.name, model: modelName(a.modelKey),
            waitEach: (a.durationSeconds || 0) / 3600, items: a.items || 0, waitHours: a.waitHours || 0,
            perItemIn: itemsIn > 0 ? (a.waitHours || 0) / itemsIn : 0, confirmed: a.workType === WORK_TYPE.WAITING,
        }));

    // ----- capacity -----
    const defaultCapacity = !!input.defaultCapacity;
    const loads = roles
        .filter((r) => hasModelledCapacity(r, defaultCapacity))
        .map((r) => ({ r, load: roleLoad(r, defaultCapacity)! }))
        .sort((a, b) => b.load - a.load);
    const overloaded = loads.filter((l) => l.load > 100);
    // external organisations' people and capacity aren't the organisation's to set
    const ownRoles = roles.filter((r) => r.type !== NODE_TYPE.EXTERNAL_ORGANISATION);
    const rolesWithoutPeople = ownRoles.filter((r) => !hasPeople(r));
    const rolesWithoutCapacity = rolesWithoutPeople.filter((r) => !r.capacitySet);
    const shortfall = (r: EpmComponent) => Math.max(0, (r.insights?.hours || 0) - (r.insights?.modelledCapacity || 0));
    const totalShortfall = overloaded.reduce((t, l) => t + shortfall(l.r), 0);

    let bottleneck: EpmBottleneck | undefined;
    if (loads.length > 0) {
        const { r, load } = loads[0];
        const demand = r.insights?.hours || 0;
        const capacity = r.insights?.modelledCapacity || 0;
        const gap = shortfall(r);
        const gapCost = gap * hourlyCost(r);
        bottleneck = {
            roleName: r.name,
            load,
            demandHours: demand,
            capacityHours: capacity,
            people: capacityPeople(r),
            shortfallHours: gap,
            shortfallCost: gapCost,
            overloaded: load > 100,
            text:
                load > 100
                    ? `<b>${r.name}</b> ${hasPeople(r) ? 'has' : 'is taken to have'} <b>${number(capacityPeople(r))} ${capacityPeople(r) === 1 ? 'person' : 'people'}</b>${hasPeople(r) ? '' : ' (none is set on it)'} giving <b>${number(capacity)} hours</b> of modelled capacity, but its actions demand <b>${number(demand)} hours</b> a year. That is a load of <b>${load}%</b>, a shortfall of <b>${number(gap)} hours</b>${gapCost > 0 ? ` (about ${money(gapCost)})` : ''}.`
                    : `The busiest role with modelled capacity is <b>${r.name}</b>: <b>${number(demand)} hours</b> of demand against <b>${number(capacity)} hours</b> of capacity, a load of <b>${load}%</b>. No role with modelled capacity is overloaded.`,
        };
    }

    // ----- risk -----
    const actionClasses = actions.map((a) => ({ a, cls: classifyAction(a, externalRoleKeys) }));
    // where an action's items go next, for AI to restructure the flow
    const actionNames = new Map(actions.map((a) => [a._key, a.name]));
    const manualActions = actionClasses.filter((x) => x.cls === 'manual');
    // actions the organisation performs itself, with a role or system attached
    const staffedActions = actionClasses.filter((x) => x.cls !== 'unassigned' && x.cls !== 'external');

    const actionsFor = (c: EpmComponent) =>
        actions.filter(
            (a) =>
                (a.roles || []).some((r) => r._key === c._key || (r.systemKeys || []).includes(c._key)) ||
                (a.systems || []).includes(c._key),
        );
    // its share of the value and revenue its work creates in the models shown, which add up across roles and systems
    const shownModels = new Set(input.modelKeys);
    // the revenue lost if it stopped, in the models shown: what the steps only it does carry, with no other route (see
    // value-contribution.ts). Insights worked out before that was known have the most revenue flowing through any of its
    // steps instead
    const revenueAtRisk = (c: EpmComponent) => {
        const insights = c.modelInsights.filter((i) => shownModels.has(i.modelKey));
        return insights.some((i) => i.atRiskRevenue !== undefined)
            ? insights.reduce((t, i) => t + (i.atRiskRevenue || 0), 0)
            : actionsFor(c).reduce((m, a) => Math.max(m, a.revenue || 0), 0);
    };
    const contributed = (c: EpmComponent) => c.modelInsights.filter((i) => shownModels.has(i.modelKey))
        .reduce((t, i) => ({ value: t.value + (i.contributedValue || 0), revenue: t.revenue + (i.contributedRevenue || 0) }), { value: 0, revenue: 0 });

    // a single point of failure is one person or one system that would put revenue at risk if it stopped: the only role on
    // steps that carry revenue, so no other can carry on, or a system such steps need, as a step needs all its systems
    // (as revenue at risk is worked out). A role
    // with no people set isn't counted, as its size is unknown, nor an external organisation, as it isn't ours to cover.
    // Insights worked out before revenue at risk was known: the only role or system of any kind on such a step
    const knowsRisk = (c: EpmComponent) => c.modelInsights.some((i) => shownModels.has(i.modelKey) && i.atRiskRevenue !== undefined);
    const singlePoints = inScope.filter((c) => {
        const onePerson = isRole(c) && c.people === 1 && !externalRoleKeys.has(c._key);
        if (!onePerson && !isSystem(c)) return false;
        if (knowsRisk(c)) return revenueAtRisk(c) > 0;
        return actionsFor(c).some((a) => {
            const performers = (a.roles || []).filter((r) => !externalRoleKeys.has(r._key)).length + (a.systems || []).length;
            return performers === 1 && (a.revenue || 0) > 0;
        });
    });
    const onlyOne = (c: EpmComponent) => (isRole(c) ? 'the only role on revenue-bearing steps' : 'needed by revenue-bearing steps');

    const riskComponents = inScope
        .filter((c) => isRole(c) || isSystem(c))
        .map((c) => ({ c, load: isRole(c) ? roleLoad(c, defaultCapacity) : undefined, atRisk: revenueAtRisk(c), spof: singlePoints.includes(c) }))
        .filter((x) => x.atRisk > 0 || (x.load || 0) > 100)
        .sort((a, b) => (b.load || 0) > 100 || (a.load || 0) > 100 ? (b.load || 0) - (a.load || 0) : b.atRisk - a.atRisk);

    const riskRows: EpmRiskRow[] = riskComponents.slice(0, 8).map(({ c, load, atRisk, spof }) => {
        const n = actionsFor(c).length;
        const reasons: string[] = [];
        if ((load || 0) > 100) reasons.push(`demand ${number(c.insights?.hours || 0)} hrs vs ${number(c.insights?.modelledCapacity || 0)} hrs capacity`);
        if (spof) reasons.push(onlyOne(c));
        reasons.push(`performs ${n} action${n === 1 ? '' : 's'}`);
        return {
            key: c._key,
            name: c.name,
            kind: isRole(c) ? 'Role' : 'System',
            why: reasons.join('; '),
            dependency: isRole(c) ? (c.people > 0 ? `${c.people} ${c.people === 1 ? 'person' : 'people'}` : 'People not set') : 'Single system',
            load: load !== undefined ? `${load}%` : isRole(c) ? 'not set' : 'n/a',
            revenueAtRisk: atRisk > 0 ? money(atRisk) : '-',
            revenueCreated: contributed(c).revenue > 0 ? money(contributed(c).revenue) : '-',
        };
    });

    // ----- option value and information value -----
    const reuse = outputs.map((o) => ({ o, uses: downstreamCount(o, edges), crossModel: edges.some((e) => e.type === EDGE_TYPE.USED_IN && (e.informationKey === o._key || e.from === o._key)) }));
    const reused = reuse.filter((r) => r.uses > 1 || r.crossModel);
    const mostReused = reuse.slice().sort((a, b) => b.uses - a.uses)[0];
    const sharedSystems = systems.filter((s) => s.modelInsights.filter((i) => i.modelKey !== ORGANISATION_MODEL_KEY).length > 1);

    const valued = outputs.filter((o) => (o.value || 0) > 0 || (o.revenue || 0) > 0);
    const topValued = outputs.slice().sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    const topDerived = unscored.slice().sort((a, b) => (b.derivedValue || 0) - (a.derivedValue || 0))[0];
    const deadEnds = zeroValueOutputs.filter(deadEnd);

    // how much of the derived value sits outside any system, said beside the derived value rather than in it
    // how each output is held: its type, and the kinds of the systems attached to it
    const systemKinds = new Map(components.filter(isSystem).map((c) => [c._key, c.systemKind || SYSTEM_KIND.BUSINESS]));
    const heldAs = (o: AnalysisNode) => informationHeld(o.heldAs ?? o.type, (o.heldBy || []).map((k) => systemKinds.get(k)).filter((k): k is SYSTEM_KIND => !!k));
    const heldOutside = derivedValueOutsideSystems(outputs.map((o) => ({ held: heldAs(o), derivedValue: o.derivedValue || 0, items: o.items || 0 })));
    const heldOutsideNote = heldOutside.outputs > 0
        ? ` ${Math.round(heldOutside.share * 100)}% of the derived value whose holding is known is held outside a controlled system, in ${heldOutside.outputs} output${heldOutside.outputs === 1 ? '' : 's'} kept in local tools, as files, on paper or passed on by word of mouth.`
        : '';
    // the information assets the outputs are states of, read across their states as the Information view reads them
    const assets: InformationAssetSummary[] = input.assets?.length
        ? buildInformationAnalysis({ nodes, edges, modelNames: input.modelNames, totals: input.totals, components: [], assets: input.assets,
            systemKinds: Object.fromEntries(systemKinds) }).assets
        : [];
    const assetName = new Map(assets.map((a) => [a.key, a.name]));
    const assetOf = (o: AnalysisNode) => (o.assetKey ? assetName.get(o.assetKey) : undefined);
    const informationState = (o: AnalysisNode): EpmInformationState =>
        o.revenue > 0 ? 'Revenue bearing' : o.value > 0 ? 'Valued' : inUse(o) ? 'In use' : 'Dead end';
    const informationRows: EpmInformationRow[] = reuse
        .slice()
        .sort((a, b) => (b.o.value || 0) - (a.o.value || 0) || (b.o.derivedValue || 0) - (a.o.derivedValue || 0) || b.uses - a.uses)
        .map(({ o, uses }) => ({
            key: o._key,
            modelKey: o.modelKey,
            name: o.name,
            model: modelName(o.modelKey),
            value: number(o.value || 0),
            userValue: o.userValue || 0,
            joinedOut: !!o.joinedOut,
            derivedValue: o.derivedValue || 0,
            derived: o.derivedValue > 0 ? `${number(o.derivedValue)} / 100` : '-',
            type: o.type,
            typeRead: o.heldAsRead ? o.heldAs : undefined,
            held: INFORMATION_HELD_LABELS[heldAs(o)] + (o.heldAsRead ? " (LINQ's read)" : ''),
            heldOutside: heldOutsideSystems(heldAs(o)),
            revenue: o.revenue > 0 ? money(o.revenue) : '-',
            reuse: `${Math.max(uses, o.consumers || 0)} downstream${o.consumerModels > 0 ? `, ${o.consumerModels} other model${o.consumerModels === 1 ? '' : 's'}` : ''}`,
            costToProduce: o.totalCost > 0 ? money(o.totalCost) : `${number(o.totalHours || 0)} hrs`,
            state: informationState(o),
            asset: assetOf(o),
        }));

    const frictionRows: EpmFrictionRow[] = frictionOutputs
        .slice()
        .sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0) || (b.totalHours || 0) - (a.totalHours || 0))
        .map((o) => ({
            key: o._key,
            modelKey: o.modelKey,
            name: o.name,
            model: modelName(o.modelKey),
            state: deadEnd(o) ? 'Dead end' : 'Unvalued',
            items: number(o.items || 0),
            hours: number(o.totalHours || 0),
            cost: money(o.totalCost || 0),
        }));

    // ----- data gaps: things the model doesn't carry, said plainly rather than invented -----
    // each with the items it is about, to show and open them; roles and systems are set up in the organisation model
    const dataGapDetails: EpmDataGap[] = [];
    const gap = (text: string, items: EpmNodeLink[] = []) => dataGapDetails.push({ text, items: [...items].sort((a, b) => a.name.localeCompare(b.name)) });
    const organisationItem = (c: EpmComponent): EpmNodeLink => ({ key: c._key, name: c.name, modelKey: ORGANISATION_MODEL_KEY });
    const modelItem = (n: AnalysisNode): EpmNodeLink => ({ key: n._key, name: n.name, modelKey: n.modelKey, model: modelName(n.modelKey) });
    const unpricedRoles = roles.filter((r) => componentCost(r) === 0 && (r.insights?.hours || 0) > 0);
    const unpricedSystems = systems.filter((s) => componentCost(s) === 0);
    if (unpricedRoles.length) gap(`${unpricedRoles.length} of ${roles.length} roles have no cost, so their time isn't priced.`, unpricedRoles.map(organisationItem));
    if (unpricedSystems.length) gap(`${unpricedSystems.length} of ${systems.length} systems have no cost.`, unpricedSystems.map(organisationItem));
    if (rolesWithoutPeople.length) gap(`${rolesWithoutPeople.length} of ${ownRoles.length} roles have no people set.`, rolesWithoutPeople.map(organisationItem));
    if (rolesWithoutCapacity.length) gap(`${rolesWithoutCapacity.length} of ${ownRoles.length} roles have no people or capacity set on the role, so ${defaultCapacity ? "their capacity is the organisation's default: a full-time year for each person." : "their capacity can't be assessed."}`, rolesWithoutCapacity.map(organisationItem));
    if (revenue === 0) gap('No output carries revenue, so revenue contribution needs one number from finance per outcome.');
    if (infoRisk.untypedHandoffs) gap(`${infoRisk.untypedHandoffs} of ${infoRisk.handoffs} pieces of information passed between steps are the general information type, so it can't be told whether they're in a system, a file, on paper or spoken. Setting their type sharpens the friction and risk read.`,
        infoRisk.untypedHandoffItems.map((i) => ({ ...i, model: modelName(i.modelKey) })));
    if (infoRisk.typeReads.length) gap(`${infoRisk.typeReads.length} piece${infoRisk.typeReads.length === 1 ? ' of information is' : 's of information are'} typed as plain information, so LINQ reads how ${infoRisk.typeReads.length === 1 ? 'it is' : 'they are'} held from the systems attached and ${infoRisk.typeReads.length === 1 ? 'its name' : 'their names'}: ${joinList(infoRisk.typeReads.slice(0, 3).map((r) => `${r.name} ${INFORMATION_HELD_LABELS[informationHeld(r.type)].toLowerCase()}`))}${infoRisk.typeReads.length > 3 ? ' and others' : ''}. Confirm or correct them.`,
        infoRisk.typeReads.map((r) => ({ key: r.key, name: r.name, modelKey: r.modelKey, model: modelName(r.modelKey) })));
    if (infoRisk.typeConflicts.length) gap(`${infoRisk.typeConflicts.length} piece${infoRisk.typeConflicts.length === 1 ? ' of information has a type' : 's of information have types'} that ${infoRisk.typeConflicts.length === 1 ? "doesn't" : "don't"} match the systems around ${infoRisk.typeConflicts.length === 1 ? 'it' : 'them'}: ${joinList(infoRisk.typeConflicts.slice(0, 3).map((c) => `${c.name} is ${c.reason}`))}${infoRisk.typeConflicts.length > 3 ? ' and others' : ''}. Correct the type, or the systems attached, so the friction and risk read is right.`,
        infoRisk.typeConflicts.map((c) => ({ key: c.key, name: c.name, modelKey: c.modelKey, model: modelName(c.modelKey) })));
    if (unscored.length) gap(`${unscored.length} of ${outputs.length} outputs in use have no value set, so their worth rests on the value LINQ derived from how they are used.`, unscored.map(modelItem));
    const dataGaps = dataGapDetails.map((g) => g.text);

    // ----- headlines -----
    const peak = loads[0];
    const headlines: EpmHeadline[] = [
        {
            label: 'Net operating value',
            value: money(net),
            detail: `Revenue ${money(revenue)} minus modelled cost ${money(cost)}`,
            tone: net >= 0 ? 'good' : 'bad',
            grounding: 'grounded',
            measure: measure(net, 'up', 'money'),
        },
        {
            label: 'Cost to operate',
            value: money(cost),
            detail: `People ${money(peopleCost)}, systems ${money(systemCost)}`,
            tone: 'neutral',
            grounding: 'grounded',
            measure: measure(cost, 'down', 'money'),
        },
        {
            label: 'Friction',
            value: frictionInMoney ? money(frictionCost) : `${number(frictionHours)} hrs`,
            detail: `${joinList([
                `${frictionOutputs.length} dead-end output${frictionOutputs.length === 1 ? '' : 's'}`,
                `${exceptionActions.length} exception step${exceptionActions.length === 1 ? '' : 's'}`,
                ...(wasteActions.length ? [`${wasteActions.length} step${wasteActions.length === 1 ? '' : 's'} not adding value`] : []),
                ...(rekeying.length ? [`${rekeying.length} re-keying step${rekeying.length === 1 ? '' : 's'}`] : []),
                ...(bridgeFriction.length ? [`${bridgeFriction.length} manual bridge${bridgeFriction.length === 1 ? '' : 's'}`] : []),
            ])}, ${totalHours ? Math.round((100 * frictionHours) / totalHours) : 0}% of modelled time`,
            tone: frictionHours > 0 ? 'bad' : 'good',
            grounding: 'grounded',
            measure: frictionInMoney ? measure(frictionCost, 'down', 'money') : measure(frictionHours, 'down', 'hours'),
        },
        {
            label: 'Peak capacity load',
            value: peak ? `${peak.load}%` : 'not set',
            detail: peak ? `${peak.r.name}: ${number(peak.r.insights?.hours || 0)} hrs demand vs ${number(peak.r.insights?.modelledCapacity || 0)} hrs capacity` : defaultCapacity ? 'No role has work to load it' : 'No role has people or capacity set',
            tone: peak ? (peak.load > 100 ? 'bad' : peak.load > 85 ? 'warn' : 'good') : 'neutral',
            grounding: 'grounded',
            measure: peak ? measure(peak.load, 'down', 'percent') : undefined,
        },
        {
            label: 'Time per item',
            value: itemsIn > 0 ? elapsed(timePerItem) : 'not known',
            detail: itemsIn === 0 ? 'No step sets how often items come in'
                : waitRows.length ? `${flowEfficiency}% of it work, ${100 - flowEfficiency}% waiting at ${waitRows.length} step${waitRows.length === 1 ? '' : 's'}`
                : 'All of it work: no step has items waiting',
            tone: itemsIn === 0 || !waitRows.length ? 'neutral' : flowEfficiency < 25 ? 'bad' : flowEfficiency < 50 ? 'warn' : 'good',
            grounding: 'grounded',
            measure: itemsIn > 0 ? measure(timePerItem, 'down', 'time') : undefined,
        },
    ];

    // ----- P&L panels -----
    const profitAndLoss: EpmPanel[] = [
        {
            title: 'Revenue contribution',
            sub: 'Output revenue, and the work that creates it',
            grounding: 'grounded',
            unit: 'money',
            lines: [
                // the revenue two ways, each a group that adds up: by the outputs it flows through, and by the steps creating
                // it (each output's revenue shared between the steps producing it, by their work), then the total
                ...breakdown('By output', 'output', revenueOutputs.map((o) => ({ name: o.name, amount: o.revenue })), 2),
                ...breakdown('By step', 'step', creatingActions.map((a) => ({ name: a.name, amount: a.contributedRevenue || 0 })), 3),
                { label: 'Annual revenue', value: money(revenue), kind: 'total', measure: measure(revenue, 'up', 'money') },
                { label: 'Relative value produced', value: number(value), measure: measure(value, 'up', 'number') },
            ],
            note: revenueOutputs.length
                ? `${revenueOutputs.length} of ${outputs.length} outputs carry revenue, allocated back from the outcomes they lead to. Each output's revenue is shared between the steps producing it by their work; exception work creates none.`
                : 'No output carries revenue yet.',
        },
        {
            title: 'Cost',
            sub: 'Role and system cost allocated by modelled time',
            grounding: 'grounded',
            unit: 'money',
            lines: [
                // the people and the systems, each with its costliest, then what they add up to
                { label: 'People', value: money(peopleCost), kind: 'group', measure: measure(peopleCost, 'down', 'money') },
                ...roleCosts.filter((r) => r.cost > 0).slice(0, 3).map((r): EpmLine => ({ label: r.c.name, value: money(r.cost), kind: 'item', measure: measure(r.cost, 'down', 'money') })),
                { label: 'Systems', value: money(systemCost), kind: 'group', measure: measure(systemCost, 'down', 'money') },
                ...systemCosts.filter((s) => s.cost > 0).slice(0, 2).map((s): EpmLine => ({ label: s.c.name, value: money(s.cost), kind: 'item', measure: measure(s.cost, 'down', 'money') })),
                { label: 'Total modelled cost', value: money(cost), kind: 'total', measure: measure(cost, 'down', 'money') },
            ],
            note: unpricedRoles.length || unpricedSystems.length
                ? `${unpricedRoles.length} roles and ${unpricedSystems.length} systems are uncosted, so this understates the true cost.`
                : 'Every role and system in scope is costed.',
        },
        {
            title: 'Friction',
            sub: 'Cost consumed without producing value',
            grounding: 'grounded',
            unit: 'money',
            lines: [
                // each kind of friction with what it comes to, and its largest items, then what they all come to
                { label: 'Dead ends', value: deadEndCost > 0 || deadEndHours === 0 ? money(deadEndCost) : `${number(deadEndHours)} hrs`, kind: 'group', measure: measure(deadEndCost, 'down', 'money') },
                ...frictionRows.slice(0, 2).map((r): EpmLine => ({ label: `${r.name} (${r.state.toLowerCase()})`, value: r.cost !== money(0) ? r.cost : `${r.hours} hrs`, kind: 'item' })),
                { label: 'Exception work', value: exceptionCost > 0 || exceptionHours === 0 ? money(exceptionCost) : `${number(exceptionHours)} hrs`, kind: 'group', measure: measure(exceptionCost, 'down', 'money') },
                ...detourRows.slice(0, 2).map((r): EpmLine => ({ label: r.leavesAt !== '-' ? `Detour from ${r.leavesAt} (${r.share}% of items)` : r.steps[0], value: detourCost(r) > 0 ? r.cost : `${r.hours} hrs`, kind: 'item' })),
                { label: 'Not value adding', value: wasteCost > 0 || wasteHours === 0 ? money(wasteCost) : `${number(wasteHours)} hrs`, kind: 'group', measure: measure(wasteCost, 'down', 'money') },
                ...wasteActions.slice(0, 2).map((a): EpmLine => ({ label: a.name, value: (a.cost || 0) > 0 ? money(a.cost || 0) : `${number(a.hours || 0)} hrs`, kind: 'item' })),
                { label: 'Re-keying', value: rekeyCost > 0 || rekeyHours === 0 ? money(rekeyCost) : `${number(rekeyHours)} hrs`, kind: 'group', measure: measure(rekeyCost, 'down', 'money') },
                ...rekeying.slice(0, 2).map((r): EpmLine => ({ label: `${r.step} (${r.medium})`, value: r.cost > 0 ? money(r.cost) : `${number(r.hours)} hrs`, kind: 'item' })),
                { label: 'Manual bridges', value: bridgeFrictionCost > 0 || bridgeFrictionHours === 0 ? money(bridgeFrictionCost) : `${number(bridgeFrictionHours)} hrs`, kind: 'group', measure: measure(bridgeFrictionCost, 'down', 'money') },
                // what each adds to the total, largest first: time counted as exception work or re-keying is there
                ...bridgeFriction.filter((r) => (r.frictionHours || 0) > 0).sort((a, b) => (b.frictionCost || 0) - (a.frictionCost || 0) || (b.frictionHours || 0) - (a.frictionHours || 0)).slice(0, 2)
                    .map((r): EpmLine => ({ label: r.title, value: (r.frictionCost || 0) > 0 ? money(r.frictionCost || 0) : `${number(r.frictionHours || 0)} hrs`, kind: 'item' })),
                { label: 'Total friction', value: money(frictionCost), kind: 'total', measure: measure(frictionCost, 'down', 'money') },
                { label: 'Time on friction', value: `${number(frictionHours)} hrs`, kind: 'total', measure: measure(frictionHours, 'down', 'hours') },
            ],
            note: frictionOutputs.length || exceptionActions.length || wasteActions.length || rekeying.length || bridgeFriction.length
                ? 'Dead ends are outputs nothing uses, that carry no value or revenue. Exception work is the work done when items leave the main flow and return to it later, such as collecting what an input is missing, or work marked as exception work. Not value adding is other work marked as waste. Re-keying is typing paper or spoken information into a system. Manual bridges are the steps not adding value that carry information from one system to another by hand or through a local tool; work on a bridge that adds value is a risk, not friction. A step that is more than one of these is counted once.'
                : 'Every output with time spent on it is used, or carries value or revenue, no work leaves the main flow, nothing is typed in from paper or by word of mouth, and no information passes between systems by hand.',
        },
        {
            title: 'Flow',
            sub: 'The time each item takes: working and waiting',
            grounding: 'grounded',
            unit: 'index',
            lines: itemsIn > 0 ? [
                // the work and the waiting, the longest waits under it, then what an item takes in all
                { label: 'Work', value: elapsed(workPerItem), kind: 'group', measure: measure(workPerItem, 'down', 'time') },
                { label: 'Waiting', value: elapsed(waitPerItem), kind: 'group', measure: measure(waitPerItem, 'down', 'time') },
                ...waitRows.slice(0, 3).map((w): EpmLine => ({ label: w.name, value: elapsed(w.perItemIn), kind: 'item' })),
                ...(waitRows.length > 3 ? [{ label: `${waitRows.length - 3} other wait${waitRows.length === 4 ? '' : 's'}`, value: elapsed(waitRows.slice(3).reduce((t, w) => t + w.perItemIn, 0)), kind: 'item' as const }] : []),
                { label: 'Time per item', value: elapsed(timePerItem), kind: 'total', measure: measure(timePerItem, 'down', 'time') },
                { label: 'Flow efficiency', value: `${flowEfficiency}%`, kind: 'total', measure: measure(flowEfficiency, 'up', 'percent') },
            ] : [{ label: 'Time per item', value: 'not known' }],
            note: itemsIn === 0
                ? 'No step sets how often items come in, so the time each item takes can\'t be worked out.'
                : `Each item's time is the work and the waiting on the steps it goes through, averaged over the ${number(itemsIn)} items a year coming in. Waiting is a step where nobody works on the items: they wait for an approval, a reply or a delivery. It costs nothing, but holds up everything after it. Flow efficiency is the share of the time that is work. Steps done side by side both count, and queues building up at busy steps aren't included: the simulation shows those.`,
        },
    ];

    // ----- balance sheet panels -----
    const balanceSheet: EpmPanel[] = [
        {
            title: 'Risk exposure',
            sub: 'Capacity, concentration, single points of failure and information outside its systems',
            grounding: 'indicative',
            unit: 'index',
            lines: [
                { label: 'Overloaded roles', value: `${overloaded.length} of ${loads.length}`, measure: measure(overloaded.length, 'down', 'count') },
                { label: 'Capacity shortfall', value: `${number(totalShortfall)} hrs`, measure: measure(totalShortfall, 'down', 'hours') },
                { label: 'Single points of failure', value: number(singlePoints.length), measure: measure(singlePoints.length, 'down', 'count') },
                { label: 'Manual actions', value: `${manualActions.length} of ${staffedActions.length}`, measure: measure(manualActions.length, 'down', 'count') },
                { label: 'Manual bridges between systems', value: number(infoRisk.bridges.length), measure: measure(infoRisk.bridges.length, 'down', 'count') },
                { label: 'Unofficial copies of information', value: number(infoRisk.copies.length), measure: measure(infoRisk.copies.length, 'down', 'count') },
                { label: 'Important work in local tools', value: number(infoRisk.localTools.length), measure: measure(infoRisk.localTools.length, 'down', 'count') },
            ],
            note: 'Manual actions fail more often than automated ones. A single point of failure is one person who is the only role on steps carrying revenue, or a system those steps need, so the revenue would be lost if it stopped. Information that leaves the systems that should hold it, by hand or through local tools, can pick up errors and leave copies taken for the real thing.',
        },
        {
            title: 'Option value',
            sub: 'Reuse and future flexibility',
            grounding: 'indicative',
            unit: 'count',
            lines: [
                { label: 'Outputs reused downstream', value: `${reused.length} of ${outputs.length}`, measure: measure(reused.length, 'up', 'count') },
                { label: 'Most reused output', value: mostReused && mostReused.uses > 0 ? mostReused.o.name : '-' },
                { label: 'Its downstream uses', value: mostReused ? number(mostReused.uses) : '0', measure: measure(mostReused?.uses || 0, 'up', 'count') },
                { label: 'Systems shared across models', value: `${sharedSystems.length} of ${systems.length}`, measure: measure(sharedSystems.length, 'up', 'count') },
            ],
            note: 'Read from the graph: outputs and systems used in several places keep future choices open. No option value is priced, so no dollar figure is asserted.',
        },
        {
            title: 'Information value',
            sub: 'Output value, reuse and waste',
            grounding: 'indicative',
            unit: 'score',
            lines: [
                { label: 'Outputs with value', value: `${valued.length} of ${outputs.length}`, measure: measure(valued.length, 'up', 'count') },
                { label: 'Highest value output', value: topValued && topValued.value > 0 ? topValued.name : '-' },
                { label: 'Valued by use only', value: number(unscored.length), measure: measure(unscored.length, 'down', 'count') },
                { label: 'Highest derived value', value: topDerived && topDerived.derivedValue > 0 ? `${topDerived.name} (${number(topDerived.derivedValue)} / 100)` : '-' },
                { label: 'Dead-end outputs', value: number(deadEnds.length), measure: measure(deadEnds.length, 'down', 'count') },
            ],
            note: 'Information value weighs reuse against the cumulative cost to produce each output. An output no one has scored carries a value LINQ derives from how it is used (0 to 100 across the organisation, from the revenue and outcomes it reaches, its reuse and the other models using it); confirm it on the output to make it grounded.',
        },
    ];

    // ----- ledger -----
    const ledger = {
        value: [
            { label: 'Revenue contribution', value: money(revenue), measure: measure(revenue, 'up', 'money') },
            { label: 'Relative value produced', value: number(value), measure: measure(value, 'up', 'number') },
            { label: 'Outputs with value', value: `${valued.length} of ${outputs.length}`, measure: measure(valued.length, 'up', 'count') },
            { label: 'Outputs reused downstream', value: `${reused.length}`, measure: measure(reused.length, 'up', 'count') },
        ],
        drag: [
            { label: 'Cost to operate', value: money(cost), measure: measure(cost, 'down', 'money') },
            { label: 'Friction', value: frictionInMoney ? money(frictionCost) : `${number(frictionHours)} hrs`, measure: frictionInMoney ? measure(frictionCost, 'down', 'money') : measure(frictionHours, 'down', 'hours') },
            { label: 'Capacity shortfall', value: `${number(totalShortfall)} hrs`, measure: measure(totalShortfall, 'down', 'hours') },
            { label: 'Single points of failure', value: number(singlePoints.length), measure: measure(singlePoints.length, 'down', 'count') },
        ],
    };

    // ----- decisions -----
    const decisions = buildDecisions({
        input, actions, outputs, overloaded: overloaded.map((l) => ({ role: l.r, load: l.load, gap: shortfall(l.r), gapCost: shortfall(l.r) * hourlyCost(l.r) })),
        frictionOutputs, deadEnd, manualActions: manualActions.map((m) => m.a), singlePoints, revenueAtRisk, actionsFor,
        unvalued: reuse.filter((r) => unscored.includes(r.o)), roleByKey: new Map(components.map((c) => [c._key, c])),
        detours: detourRows.map((r) => ({ row: r, cost: detourCost(r), hours: detourHours(r) })),
        informationRisks: infoRisk.rows,
        rekeying,
        flow: { waits: waitRows, workPerItem, timePerItem },
    });

    // ----- summary -----
    const signals: string[] = [];
    if (overloaded.length) signals.push(`a capacity issue on <b>${overloaded[0].r.name}</b> (${overloaded[0].load}% load)`);
    if (frictionOutputs.length || exceptionActions.length || wasteActions.length || rekeying.length || bridgeFriction.length) {
        const parts = [frictionOutputs.length ? `${frictionOutputs.length} dead end${frictionOutputs.length === 1 ? '' : 's'}` : '', detourRows.length ? `${detourRows.length} detour${detourRows.length === 1 ? '' : 's'} from the main flow` : '',
            wasteActions.length ? `${wasteActions.length} step${wasteActions.length === 1 ? '' : 's'} not adding value` : '',
            rekeying.length ? `${rekeying.length} step${rekeying.length === 1 ? '' : 's'} re-keying paper or spoken information` : '',
            bridgeFriction.length ? `${bridgeFriction.length} manual bridge${bridgeFriction.length === 1 ? '' : 's'} between systems` : ''].filter((p) => p);
        signals.push(`friction of <b>${frictionCost > 0 ? money(frictionCost) : number(frictionHours) + ' hours'}</b> on ${joinList(parts)}`);
    }
    if (waitRows.length && itemsIn > 0) signals.push(`items spending <b>${elapsed(waitPerItem)}</b> of their <b>${elapsed(timePerItem)}</b> waiting, longest at <b>${waitRows[0].name}</b>`);
    if (singlePoints.length) signals.push(`<b>${singlePoints.length}</b> single point${singlePoints.length === 1 ? '' : 's'} of failure`);
    if (manualActions.length) signals.push(`<b>${manualActions.length} manual actions</b>`);
    if (infoRisk.bridges.length) signals.push(`<b>${infoRisk.bridges.length}</b> manual bridge${infoRisk.bridges.length === 1 ? '' : 's'} between systems`);
    if (infoRisk.copies.length || infoRisk.localTools.length) {
        const n = infoRisk.copies.length + infoRisk.localTools.length;
        signals.push(`<b>${n}</b> place${n === 1 ? '' : 's'} where information is kept outside a system of record`);
    }
    const posture = net > 0 ? 'so it reads as a <b>net contributor</b>' : net < 0 ? 'so it reads as a <b>net cost</b>' : 'with no net value yet';
    const summary =
        `${input.scopeName} produces <b>${money(revenue)}</b> of revenue a year for <b>${money(cost)}</b> of modelled cost, ${posture}.` +
        (signals.length ? ` The signals to act on are ${joinList(signals)}.` : ' No capacity, friction or concentration signals stand out.');

    // ----- evidence for AI decisions: the figures above, the nodes behind them and the signals to address -----
    const componentName = new Map(components.map((c) => [c._key, c.name]));
    // the longest waits, kept in whatever else is cut to fit
    const longestWaits = new Set(waitRows.slice(0, 3).map((w) => w.key));
    const waitSignals = waitRows.filter((w) => longestWaits.has(w.key)).map((w) => ({ id: `waits:${w.key}`, description: `Items wait ${elapsed(w.waitEach)} at ${w.name}, where nobody works on them: ${Math.round(w.waitHours)} hours a year of waiting over ${Math.round(w.items)} items${timePerItem > 0 ? `, ${Math.round((100 * w.perItemIn) / timePerItem)}% of the ${elapsed(timePerItem)} each item takes` : ''}${w.confirmed ? '' : ' (LINQ reads it as a wait, as no role or system performs it)'}.` }));
    const signalEvidence = [
        ...overloaded.map((l) => ({ id: `overloaded:${l.r._key}`, description: `${l.r.name} is loaded at ${l.load}%: ${Math.round(l.r.insights?.hours || 0)} hours of demand against ${Math.round(l.r.insights?.modelledCapacity || 0)} hours of capacity.` })),
        ...frictionOutputs.map((o) => ({ id: `friction:${o._key}`, description: `${o.name} takes ${Math.round(o.totalHours || 0)} hours and ${Math.round(o.totalCost || 0)} ${input.currency} a year to produce but carries no value or revenue${deadEnd(o) ? ', and nothing uses it' : ''}.` })),
        ...manualActions.slice().sort((a, b) => (b.a.hours || 0) - (a.a.hours || 0)).slice(0, 5).map(({ a }) => ({ id: `manual:${a._key}`, description: `${a.name} is performed manually for ${Math.round(a.items || 0)} items a year, taking ${Math.round(a.hours || 0)} hours.` })),
        ...singlePoints.map((c) => ({ id: `singlePoints:${c._key}`, description: `${c.name} is ${onlyOne(c)}, with ${Math.round(revenueAtRisk(c))} ${input.currency} of revenue at risk if it stopped.` })),
        ...rekeying.slice(0, 3).map((r) => ({ id: `rekeying:${r.key}`, description: `${r.step} types ${r.information} into ${r.systems} from ${r.medium}: ${Math.round(r.items)} items and ${Math.round(r.hours)} hours a year of staff time.` })),
        ...infoRisk.rows.slice(0, 6).map((r) => ({ id: `${r.kind === 'bridge' ? 'bridges' : r.kind === 'copy' ? 'copies' : 'localTools'}:${r.key}`, description: r.detail.replace(/<[^>]+>/g, '') })),
        // the assets in use that no one has valued, the costliest first, and the valuable ones with a state kept outside a
        // controlled system
        ...assets.filter((a) => a.state === 'In use' && (a.cost > 0 || a.hours > 0)).sort((a, b) => b.cost - a.cost || b.hours - a.hours).slice(0, 3)
            .map((a) => ({ id: `asset:${a.key}`, description: `The information asset ${a.name} is used by ${a.consumers} ${a.consumers === 1 ? 'action or outcome' : 'actions and outcomes'}${a.consumerModels ? ` in ${a.consumerModels} other ${a.consumerModels === 1 ? 'model' : 'models'}` : ''} and takes ${Math.round(a.hours)} hours and ${Math.round(a.cost)} ${input.currency} a year across its ${a.numberOfStates} states, but no value or revenue is set on any of them${a.derivedValue ? `; its derived value is ${a.derivedValue} of 100 among the organisation's assets` : ''}.` })),
        ...assets.filter((a) => a.heldOutside > 0 && (a.value > 0 || a.revenue > 0 || (a.derivedValue || 0) > 0)).slice(0, 3)
            .map((a) => ({ id: `assetHeld:${a.key}`, description: `${a.heldOutside} of the ${a.states.length} states of the information asset ${a.name} shown are kept outside a controlled system (in a local tool, as a file, on paper or by word of mouth), though it ${a.revenue > 0 ? `bears ${Math.round(a.revenue)} ${input.currency} of revenue a year` : a.value > 0 ? 'carries value' : `has a derived value of ${a.derivedValue} of 100`}.` })),
        // the assets their register or holding puts at risk: valuable with no owner, held outside their system of record,
        // or sensitive and kept outside a controlled system
        ...assets.filter((a) => a.risks.length).slice(0, 3)
            .map((a) => ({ id: `assetRisk:${a.key}`, description: `The information asset ${a.name} is at risk: ${a.risks.map((r) => r.text.charAt(0).toLowerCase() + r.text.slice(1)).join('; ')}.` })),
        ...(unscored.length ? [{ id: 'unvalued:all', description: `${unscored.length} outputs are in use but have no value set, so their worth rests on the value LINQ derived from how they are used (derivedValue, 0 to 100).` }] : []),
        ...detourRows.slice(0, 5).map((r) => ({ id: `detours:${r.key}`, description: r.leavesAt !== '-'
            ? `${r.share}% of the items leaving ${r.leavesAt} take a detour through ${joinList(r.steps)} before returning at ${r.returnsAt}: exception work of ${r.hours} hours and ${Math.round(detourCost(r))} ${input.currency} a year${r.confirmed ? ', confirmed by the user' : ''}.`
            : `${joinList(r.steps)} is marked as exception work: ${r.hours} hours and ${Math.round(detourCost(r))} ${input.currency} a year.` })),
    ];
    const evidence: EpmDecisionEvidence = {
        scope: input.scopeName,
        workspace: input.workspaceName,
        currency: input.currency,
        totals: {
            revenue: Math.round(revenue), relativeValue: Math.round(value), cost: Math.round(cost), peopleCost: Math.round(peopleCost),
            systemCost: Math.round(systemCost), netOperatingValue: Math.round(net), frictionCost: Math.round(frictionCost),
            deadEndCost: Math.round(deadEndCost), exceptionCost: Math.round(exceptionCost), exceptionHours: Math.round(exceptionHours),
            notValueAddingCost: Math.round(wasteCost), notValueAddingHours: Math.round(wasteHours),
            rekeyCost: Math.round(rekeyCost), rekeyHours: Math.round(rekeyHours), bridgeCost: Math.round(bridgeFrictionCost), bridgeHours: Math.round(bridgeFrictionHours),
            frictionHours: Math.round(frictionHours), peopleHours: Math.round(actions.filter((a) => (a.roles || []).length > 0).reduce((t, a) => t + (a.hours || 0), 0)),
            ...(itemsIn > 0 ? { workHoursPerItem: round2(workPerItem), waitHoursPerItem: round2(waitPerItem), flowEfficiencyPercent: flowEfficiency } : {}),
        },
        roles: roles.slice(0, EPM_EVIDENCE_LIMITS.roles).map((r) => ({
            name: r.name,
            people: r.people > 0 ? r.people : null,
            demandHours: Math.round(r.insights?.hours || 0),
            capacityHours: hasModelledCapacity(r, defaultCapacity) ? Math.round(r.insights!.modelledCapacity) : null,
            loadPercent: roleLoad(r, defaultCapacity) ?? null,
            cost: Math.round(componentCost(r)),
            actions: actionsFor(r).length,
            revenueContributed: Math.round(contributed(r).revenue),
            valueContributed: Math.round(contributed(r).value),
            revenueAtRisk: Math.round(revenueAtRisk(r)),
        })),
        systems: systems.slice(0, EPM_EVIDENCE_LIMITS.systems).map((s) => ({
            name: s.name,
            actions: actionsFor(s).length,
            models: s.modelInsights.length,
            cost: Math.round(componentCost(s)),
            revenueContributed: Math.round(contributed(s).revenue),
            valueContributed: Math.round(contributed(s).value),
            revenueAtRisk: Math.round(revenueAtRisk(s)),
        })),
        // the heaviest actions, with the longest waits first: they have no hours, but a decision can shorten them
        actions: actionClasses
            .slice()
            .sort((a, b) => Number(longestWaits.has(b.a._key)) - Number(longestWaits.has(a.a._key)) || (b.a.hours || 0) - (a.a.hours || 0))
            .slice(0, EPM_EVIDENCE_LIMITS.actions)
            .map(({ a, cls }) => ({
                name: a.name,
                model: modelName(a.modelKey),
                performedBy: cls,
                roles: (a.roles || []).map((r) => componentName.get(r._key) || '').filter((n) => n),
                systems: (a.systems || []).map((s) => componentName.get(s) || '').filter((n) => n),
                itemsPerYear: Math.round(a.items || 0),
                hours: Math.round(a.hours || 0),
                cost: Math.round(a.cost || 0),
                revenueFlowing: Math.round(a.revenue || 0),
                value: Math.round(a.value || 0),
                revenueContributed: Math.round(a.contributedRevenue || 0),
                valueContributed: Math.round(a.contributedValue || 0),
                revenueAtRisk: Math.round(a.atRiskRevenue || 0),
                workKind: a.workKind,
                ...(a.workKind === WORK_TYPE.WAITING ? { waitHoursEach: round2((a.durationSeconds || 0) / 3600), waitHours: Math.round(a.waitHours || 0) } : {}),
                next: nextSteps(a, edges, actionNames),
            })),
        outputs: reuse.slice(0, EPM_EVIDENCE_LIMITS.outputs).map(({ o, uses }) => ({
            name: o.name,
            model: modelName(o.modelKey),
            value: Math.round(o.value || 0),
            revenue: Math.round(o.revenue || 0),
            userValue: o.userValue > 0 ? o.userValue : null,
            derivedValue: Math.round(o.derivedValue || 0),
            held: heldAs(o),
            itemsPerYear: Math.round(o.items || 0),
            downstreamUses: Math.max(uses, o.consumers || 0),
            modelsUsedIn: o.consumerModels || 0,
            outcomesReached: o.outcomesReached || 0,
            hoursToProduce: Math.round(o.totalHours || 0),
            costToProduce: Math.round(o.totalCost || 0),
            state: informationState(o),
            ...(assetOf(o) ? { asset: assetOf(o) } : {}),
        })),
        ...(assets.length ? { assets: assets.slice(0, EPM_EVIDENCE_LIMITS.assets).map((a) => ({
            name: a.name,
            description: a.description,
            states: a.numberOfStates,
            models: a.models,
            itemsPerYear: Math.round(a.items),
            value: Math.round(a.value),
            revenue: Math.round(a.revenue),
            derivedValue: a.derivedValue ?? null,
            uses: a.consumers,
            modelsUsedIn: a.consumerModels,
            outcomesReached: a.outcomesReached,
            externalOrganisations: a.externalConsumers,
            hoursOnStates: Math.round(a.hours),
            costOfStates: Math.round(a.cost),
            statesHeldOutside: a.heldOutside,
            state: a.state,
            owner: a.owner || null,
            classification: a.classification || null,
            systemOfRecord: a.systemOfRecord || null,
            risks: a.risks.map((r) => r.text),
        })) } : {}),
        detours: detourRows.slice(0, EPM_EVIDENCE_LIMITS.detours).map((r) => ({
            leavesAt: r.leavesAt, returnsAt: r.returnsAt, model: r.model, steps: r.steps, shareOfItems: r.share,
            itemsPerYear: Math.round(Math.max(...detourGroups.get(r.key)!.map((a) => a.items || 0), 0)), hours: Math.round(detourHours(r)), cost: Math.round(detourCost(r)), confirmed: r.confirmed,
        })),
        signals: [...signalEvidence.slice(0, EPM_EVIDENCE_LIMITS.signals - waitSignals.length), ...waitSignals],
        dataGaps,
    };

    return {
        scopeName: input.scopeName,
        workspaceName: input.workspaceName,
        summary,
        headlines,
        bottleneck,
        profitAndLoss,
        balanceSheet,
        ledger,
        frictionRows,
        detourRows,
        wasteRows,
        waitRows,
        frictionNote: frictionRows.length || detourRows.length || wasteRows.length || rekeying.length || bridgeFriction.length
            ? `Friction is the time and cost spent on dead ends (outputs nothing uses, that carry no value or revenue), on exception work (items that leave the main flow and return later, or work marked as exception work), on other work marked as not adding value, on re-keying (typing paper or spoken information into a system) and on manual bridges (carrying information between systems by hand or through a local tool). A step that is more than one of these is counted once. Together they take ${number(frictionHours)} hours a year.`
            : 'No friction: every output with time spent on it is used, or carries value or revenue, and no work leaves the main flow.',
        riskRows,
        riskNote: 'Ranked by capacity overload first, then by the revenue at risk: what would be lost if the component stopped, as the work only it does has no other route. Load is shown where the role has people or capacity set, or the organisation has a default capacity.',
        informationRisks: infoRisk.rows,
        rekeyRows: rekeying,
        informationRiskNote: infoRisk.rows.length
            ? `Where information leaves the systems that should hold it. A manual bridge's steps that don't add value are friction too, in the friction total, counted once where a step is also exception work or re-keying.${infoRisk.rows.some((r) => r.systemsSuggested) ? ' Some systems\' kinds are LINQ\'s read of their names: confirm them on each system to firm these up.' : ''}`
            : 'No information leaves its systems by hand or through local tools, and nothing other work uses is kept outside a system of record.',
        informationRows,
        informationNote: `${valued.length} of ${outputs.length} outputs carry a value score or revenue. Derived value ranks each output 0 to 100 across the organisation by how it is used: the revenue and outcomes it reaches, its reuse and the other models using it. Dead ends are outputs nothing uses.${heldOutsideNote}`,
        decisions,
        dataGaps,
        dataGapDetails,
        evidence,
        // the people and systems' costs in each model, as the P&L adds them up
        valueProfile: input.modelKeys.length > 1 && input.modelValues ? buildValueProfile({
            modelKeys: input.modelKeys, modelName, nodes, edges, inUse,
            created: (key) => input.modelValues![key] || { revenue: 0, value: 0 },
            cost: (key) => [...roles, ...systems].reduce((t, c) => t + c.modelInsights.filter((i) => i.modelKey === key).reduce((s, i) => s + (i.allocatedCostsByType?.['total'] || 0), 0), 0),
        }) : [],
        signals: {
            overloaded: overloaded.map((l) => l.r._key),
            friction: frictionOutputs.map((o) => o._key),
            manual: manualActions.map((m) => m.a._key),
            singlePoints: singlePoints.map((c) => c._key),
            unvalued: unscored.map((o) => o._key),
            detours: detourRows.map((r) => r.key),
            bridges: infoRisk.bridges.map((r) => r.key),
            copies: infoRisk.copies.map((r) => r.key),
            localTools: infoRisk.localTools.map((r) => r.key),
            rekeying: rekeying.map((r) => r.key),
            // every asset each signal is about, not only those shown to AI, so a future state is checked against them all
            asset: assets.filter((a) => a.state === 'In use' && (a.cost > 0 || a.hours > 0)).map((a) => a.key),
            assetHeld: assets.filter((a) => a.heldOutside > 0 && (a.value > 0 || a.revenue > 0 || (a.derivedValue || 0) > 0)).map((a) => a.key),
            assetRisk: assets.filter((a) => a.risks.length).map((a) => a.key),
            waits: waitRows.map((w) => w.key),
        },
    };
};

// ---------- decisions ----------

interface DecisionContext {
    input: EpmScorecardInput;
    actions: AnalysisNode[];
    outputs: AnalysisNode[];
    overloaded: { role: EpmComponent; load: number; gap: number; gapCost: number }[];
    frictionOutputs: AnalysisNode[];
    deadEnd: (o: AnalysisNode) => boolean;
    manualActions: AnalysisNode[];
    singlePoints: EpmComponent[];
    revenueAtRisk: (c: EpmComponent) => number;
    actionsFor: (c: EpmComponent) => AnalysisNode[];
    unvalued: { o: AnalysisNode; uses: number }[];
    roleByKey: Map<string, EpmComponent>;
    detours: { row: EpmDetourRow; cost: number; hours: number }[];     // costliest first
    informationRisks: EpmInformationRiskRow[];                          // most serious first
    rekeying: EpmRekeyRow[];                                            // the most time first
    flow: { waits: EpmWaitRow[]; workPerItem: number; timePerItem: number };   // the most waiting first
}

const EFFORT_WEIGHT = { Low: 1, Medium: 2, High: 3 };
const CONFIDENCE_WEIGHT = { High: 1, Medium: 0.7, Low: 0.4, 'n/a': 1 };

// default chance a year that a sole performer fails and stops the revenue path: people fail more often than systems
export const FAILURE_RATE = { person: 0.1, system: 0.02 };

// Priority = Impact x Confidence / Effort, scaled so the strongest decision scores 100.
// Impact is the share of the process a decision moves: of its people time for time decisions, and of its revenue
// (as an expected, probability-weighted loss) for risk decisions, so that different kinds of decision compare fairly.
interface Candidate extends Omit<EpmDecision, 'sequence' | 'priority' | 'dependsOn' | 'gate'> {
    impact: number;
    enablesAutomation?: boolean;
}

const buildDecisions = (ctx: DecisionContext): EpmDecision[] => {
    const { money, number } = ctx.input;
    const candidates: Candidate[] = [];
    const peopleHours = ctx.actions.filter((a) => (a.roles || []).length > 0).reduce((t, a) => t + (a.hours || 0), 0) || 1;
    const timeShare = (hours: number) => hours / peopleHours;
    const revenueShare = (amount: number) => (ctx.input.totals.revenue > 0 ? amount / ctx.input.totals.revenue : 0);
    const roleNames = (a: AnalysisNode) =>
        (a.roles || []).map((r) => ctx.roleByKey.get(r._key)).filter((r): r is EpmComponent => !!r);

    // relieve each overloaded role
    ctx.overloaded.slice(0, 2).forEach(({ role, load, gap, gapCost }) => {
        const heaviest = ctx.actionsFor(role).slice().sort((a, b) => (b.hours || 0) - (a.hours || 0))[0];
        candidates.push({
            lever: 'automate',
            title: `Relieve the capacity overload on ${role.name}`,
            signal: `${role.name} is loaded at ${load}%: ${number(role.insights?.hours || 0)} hours of demand against ${number(role.insights?.modelledCapacity || 0)} hours of capacity, a shortfall of ${number(gap)} hours.`,
            rationale: heaviest
                ? `Its heaviest action is ${heaviest.name} (${number(heaviest.hours)} hours a year). Automate or redistribute that work, or add capacity, so the role stops being the constraint on the process.`
                : 'Automate or redistribute its work, or add capacity, so the role stops being the constraint on the process.',
            nodes: [{ kind: 'role', name: role.name }, ...(heaviest ? [{ kind: 'action' as const, name: heaviest.name }] : [])],
            impacts: [{ dimension: 'Capacity', movement: `+${number(gap)} hrs` }, ...(gapCost > 0 ? [{ dimension: 'Cost', movement: `-${money(gapCost)}` }] : []), { dimension: 'Load', movement: `${load}% to 100% or less` }],
            effort: 'Medium',
            confidence: 'High',
            grounding: 'grounded',
            impact: timeShare(gap),
            enablesAutomation: true,
            subjects: [{ kind: 'overloaded', key: role._key }],
        });
    });

    // eliminate the largest friction outputs
    ctx.frictionOutputs
        .slice()
        .sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0) || (b.totalHours || 0) - (a.totalHours || 0))
        .slice(0, 2)
        .forEach((o) => {
            const dead = ctx.deadEnd(o);
            candidates.push({
                lever: 'eliminate',
                title: dead ? `Eliminate the work producing ${o.name}` : `Remove or redirect ${o.name}`,
                signal: `${o.name} takes ${number(o.totalHours || 0)} hours${o.totalCost > 0 ? ` and ${money(o.totalCost)}` : ''} a year to produce but carries no value or revenue${dead ? ', and nothing downstream uses it' : ''}.`,
                rationale: dead
                    ? 'A dead-end output is value destroyed in transit. Stop producing it, or route it to where it creates value.'
                    : 'Either the output has real value that isn\'t scored, or the work behind it is rework or an error path. Find out which, then remove the waste or score the value.',
                nodes: [{ kind: 'info', name: o.name }],
                impacts: [{ dimension: 'Friction', movement: o.totalCost > 0 ? `-${money(o.totalCost)}` : `-${number(o.totalHours || 0)} hrs` }],
                effort: 'Medium',
                confidence: dead ? 'High' : 'Medium',
                grounding: 'grounded',
                impact: timeShare(o.totalHours || 0),
                subjects: [{ kind: 'friction', key: o._key }],
            });
        });

    // automate the heaviest manual work, unless its value suggests judgement worth protecting and amplifying
    const valuePerHour = (a: AnalysisNode) => (a.hours ? (a.revenue || a.value || 0) / a.hours : 0);
    const medianValuePerHour = median(ctx.actions.filter((a) => a.hours > 0).map(valuePerHour));
    ctx.manualActions
        .filter((a) => (a.hours || 0) > 0)
        .sort((a, b) => (b.hours || 0) - (a.hours || 0))
        .slice(0, 2)
        .forEach((a) => {
            const performers = roleNames(a);
            const highValue = medianValuePerHour > 0 && valuePerHour(a) > 2 * medianValuePerHour;
            candidates.push(
                highValue
                    ? {
                          lever: 'amplify',
                          title: `Amplify ${a.name} rather than automate it`,
                          signal: `${a.name} is manual, ${number(a.hours)} hours a year, and carries well above the typical value per hour of the process.`,
                          rationale: 'Its value may come from human judgement. Give the people doing it better information and tools instead of replacing them, and protect the judgement that creates the value.',
                          nodes: [{ kind: 'action', name: a.name }, ...performers.map((r) => ({ kind: 'role' as const, name: r.name }))],
                          impacts: [{ dimension: 'Value', movement: 'protected and scaled' }],
                          effort: 'Medium',
                          confidence: 'Low',
                          grounding: 'indicative',
                          impact: timeShare(a.hours / 2),
                          subjects: [{ kind: 'manual', key: a._key }],
                      }
                    : {
                          lever: 'automate',
                          title: `Automate ${a.name}`,
                          signal: `${a.name} is performed manually for ${number(a.items || 0)} items a year, taking ${number(a.hours)} hours.`,
                          rationale: 'High-volume manual work with ordinary value per hour is the clearest automation candidate. Leave people to handle the exceptions.',
                          nodes: [{ kind: 'action', name: a.name }, ...performers.map((r) => ({ kind: 'role' as const, name: r.name }))],
                          impacts: [{ dimension: 'Capacity', movement: `+${number(a.hours)} hrs` }, ...(a.cost > 0 ? [{ dimension: 'Cost', movement: `-${money(a.cost)}` }] : [])],
                          effort: a.hours > 1000 ? 'High' : 'Medium',
                          confidence: 'Medium',
                          grounding: 'grounded',
                          impact: timeShare(a.hours),
                          enablesAutomation: true,
                          subjects: [{ kind: 'manual', key: a._key }],
                      },
            );
        });

    // reduce dependence on single points of failure
    const failureRate = (c: EpmComponent) => (c.superType === NODE_SUPERTYPE.ROLE ? FAILURE_RATE.person : FAILURE_RATE.system);
    const expectedLoss = (c: EpmComponent) => ctx.revenueAtRisk(c) * failureRate(c);
    ctx.singlePoints
        .slice()
        .sort((a, b) => expectedLoss(b) - expectedLoss(a))
        .slice(0, 1)
        .forEach((c) => {
            const atRisk = ctx.revenueAtRisk(c);
            candidates.push({
                lever: 'protect',
                title: `Reduce the dependency on ${c.name}`,
                signal: `${c.name} is ${c.superType === NODE_SUPERTYPE.ROLE ? 'the only role on revenue-bearing steps' : 'needed by revenue-bearing steps'}, with ${money(atRisk)} of revenue at risk if it stopped. At a default ${Math.round(100 * failureRate(c))}% chance of failure a year, the expected loss is ${money(expectedLoss(c))}.`,
                rationale: c.superType === NODE_SUPERTYPE.ROLE
                    ? 'Cross-train a second person or document the work, so an absence doesn\'t stop the revenue path.'
                    : 'Put a fallback or monitoring in place, so an outage doesn\'t stop the revenue path.',
                nodes: [{ kind: c.superType === NODE_SUPERTYPE.ROLE ? 'role' : 'system', name: c.name }],
                impacts: [{ dimension: 'Expected loss', movement: `-${money(expectedLoss(c))}` }, { dimension: 'SPOFs', movement: '-1' }],
                effort: 'Low',
                confidence: 'Medium',
                grounding: 'indicative',
                impact: revenueShare(expectedLoss(c)),
                subjects: [{ kind: 'singlePoints', key: c._key }],
            });
        });

    // take the costliest detours out of the process by fixing their cause at the source
    ctx.detours.slice(0, 2).forEach(({ row, cost, hours }) => {
        const onDetour = row.leavesAt !== '-';
        candidates.push({
            lever: 'resequence',
            title: onDetour ? `Fix at source what sends items from ${row.leavesAt} on a detour` : `Take the exception work out of ${row.steps[0]}`,
            signal: onDetour
                ? `${row.share}% of the items leaving ${row.leavesAt} take a detour through ${joinList(row.steps)} before returning at ${row.returnsAt}: ${number(hours)} hours${cost > 0 ? ` and ${money(cost)}` : ''} a year of exception work.`
                : `${joinList(row.steps)} is exception work: ${number(hours)} hours${cost > 0 ? ` and ${money(cost)}` : ''} a year.`,
            rationale: onDetour
                ? `Items leave the main flow because something is missing or wrong when they arrive. Validate or collect it at ${row.leavesAt === 'an earlier step' ? 'the source' : row.leavesAt} so items stay on the main flow${row.share > 0 ? `: every 1% fewer items on the detour saves about ${cost > 0 ? money(cost / row.share) : number(hours / row.share) + ' hours'} a year` : ''}. If the detour is part of the process for some items, set its steps to Necessary or Value adding.`
                : 'Find what sends items into this work and fix it where they come in, so the work is not needed, or set the steps to Necessary or Value adding if they are part of the process.',
            nodes: row.steps.slice(0, 3).map((name) => ({ kind: 'action' as const, name })),
            impacts: [
                { dimension: 'Friction', movement: cost > 0 ? `-${money(cost)}` : `-${number(hours)} hrs` },
                ...(onDetour ? [{ dimension: 'Items on detour', movement: `${row.share}% to 0%` }] : []),
                ...(onDetour && row.share > 0 ? [{ dimension: 'Per 1% fewer items', movement: cost > 0 ? `-${money(cost / row.share)}` : `-${number(hours / row.share)} hrs` }] : []),
            ],
            effort: 'Medium',
            confidence: row.confirmed ? 'High' : 'Medium',
            grounding: row.confirmed ? 'grounded' : 'indicative',
            impact: timeShare(hours),
            subjects: [{ kind: 'detours', key: row.key }],
        });
    });

    // shorten the longest waits: they cost nothing, but hold up every item and what it leads to. Weighed by their share of
    // the time each item takes, and less than work, as no hours or money come back
    const { waits, workPerItem, timePerItem } = ctx.flow;
    const efficiency = (time: number) => (time > 0 ? Math.round((100 * workPerItem) / time) : 100);
    waits.filter((w) => w.perItemIn > 0).slice(0, 2).forEach((w) => {
        const share = timePerItem > 0 ? w.perItemIn / timePerItem : 0;
        candidates.push({
            lever: 'eliminate',
            title: `Shorten the wait: ${w.name}`,
            signal: `Items wait ${elapsed(w.waitEach)} at ${w.name}, ${Math.round(100 * share)}% of the ${elapsed(timePerItem)} each item takes: ${number(w.waitHours)} hours a year of waiting over ${number(w.items)} items.`,
            rationale: 'Find what items wait for here (an approval, a reply, a batch that runs now and then, a hand-off to another team) and shorten it: agree a turnaround, approve within limits automatically, run it more often, or do it alongside the steps around it rather than before them. Waiting costs no staff time, but delays everything after it, and the revenue and outcomes it leads to.',
            nodes: [{ kind: 'action', name: w.name }],
            impacts: [
                { dimension: 'Time per item', movement: `-${elapsed(w.perItemIn)}` },
                { dimension: 'Flow efficiency', movement: `${efficiency(timePerItem)}% to ${efficiency(timePerItem - w.perItemIn)}%` },
            ],
            effort: 'Medium',
            confidence: w.confirmed ? 'High' : 'Medium',
            grounding: w.confirmed ? 'grounded' : 'indicative',
            impact: share / 4,
            subjects: [{ kind: 'waits', key: w.key }],
        });
    });

    // close the manual bridges between systems: connect them directly, so information isn't moved and fixed by hand
    ctx.informationRisks.filter((r) => r.kind === 'bridge').slice(0, 2).forEach((r) => {
        candidates.push({
            lever: 'automate',
            title: `Replace the manual bridge: ${r.title}`,
            signal: r.detail,
            rationale: 'Connect the systems directly, or have the receiving system read from the source, so information isn\'t exported and fixed up by hand. It removes the friction and the chance of errors and stray copies together.',
            nodes: [],
            // the friction it removes is the work on it that doesn't add value; the rest is still done, in a system
            impacts: [...((r.frictionHours || 0) > 0 ? [{ dimension: 'Friction', movement: (r.frictionCost || 0) > 0 ? `-${money(r.frictionCost || 0)}` : `-${number(r.frictionHours || 0)} hrs` }] : []), { dimension: 'Manual bridges', movement: '-1' }],
            effort: 'Medium',
            confidence: r.systemsSuggested ? 'Medium' : 'High',
            grounding: 'grounded',
            impact: timeShare(r.hours) * (r.severity === 'high' ? 1.5 : 1),
            enablesAutomation: true,
            subjects: [{ kind: 'bridges', key: r.key }],
        });
    });

    // capture what is re-keyed digitally where it starts, so no one types it in from paper or from what they were told
    ctx.rekeying.slice(0, 2).forEach((r) => {
        candidates.push({
            lever: 'automate',
            title: `Capture ${r.information} digitally where it starts`,
            signal: `${r.step} types ${r.information} into ${r.systems} from ${r.medium}: ${number(r.items)} items and ${number(r.hours)} hours a year.`,
            rationale: 'A form, a portal or an integration at the source puts the information straight into the system, removing the typing and the mistakes it brings.',
            nodes: [{ kind: 'action', name: r.step }],
            impacts: [{ dimension: 'Friction', movement: r.cost > 0 ? `-${money(r.cost)}` : `-${number(r.hours)} hrs` }],
            effort: 'Medium',
            confidence: 'High',
            grounding: 'grounded',
            impact: timeShare(r.hours),
            enablesAutomation: true,
            subjects: [{ kind: 'rekeying', key: r.key }],
        });
    });

    // keep important information in a system of record, rather than in copies and local tools
    ctx.informationRisks.filter((r) => r.kind !== 'bridge').slice(0, 2).forEach((r) => {
        candidates.push({
            lever: 'protect',
            title: r.kind === 'copy' ? `Keep the authoritative copy in a system of record: ${r.title}` : `Move the important work out of a local tool: ${r.title.replace(/^Important work done in /, '')}`,
            signal: r.detail,
            rationale: r.kind === 'copy'
                ? 'Save it where the authoritative copy lives, or mark the copy clearly as a working copy, so no one acts on an out-of-date version.'
                : 'Put this information in a system of record with controls around who changes it, and keep the local tool for working copies.',
            nodes: [],
            impacts: [{ dimension: 'Information risk', movement: 'lower' }],
            effort: r.kind === 'copy' ? 'Low' : 'High',
            confidence: r.systemsSuggested ? 'Low' : 'Medium',
            grounding: 'indicative',
            impact: timeShare(r.hours) * (r.severity === 'high' ? 0.8 : 0.4),
            subjects: [{ kind: r.kind === 'copy' ? 'copies' : 'localTools', key: r.key }],
        });
    });

    // confirm the derived value of the information that is in use but no one has scored
    if (ctx.unvalued.length) {
        const top = ctx.unvalued.slice().sort((a, b) => (b.o.derivedValue || 0) - (a.o.derivedValue || 0) || b.uses - a.uses).slice(0, 3);
        const lead = top[0].o;
        candidates.push({
            lever: 'leverage',
            title: 'Confirm the derived value of the information in use',
            signal: `${ctx.unvalued.length} output${ctx.unvalued.length === 1 ? ' is' : 's are'} in use but ha${ctx.unvalued.length === 1 ? 's' : 've'} no value set, so ${ctx.unvalued.length === 1 ? 'its' : 'their'} worth rests on the value LINQ derived from how ${ctx.unvalued.length === 1 ? 'it is' : 'they are'} used${lead.derivedValue > 0 ? `, led by ${lead.name} at ${number(lead.derivedValue)} out of 100` : ''}.`,
            rationale: 'LINQ ranks each output by the revenue and outcomes it reaches, its reuse and the other models using it. Confirm or adjust these values on the outputs (the suggestion is shown on each) so the information lens rests on agreed values, and so that automating the work behind them weighs what they are worth.',
            nodes: top.map(({ o }) => ({ kind: 'info' as const, name: o.name })),
            impacts: [{ dimension: 'Info value', movement: `${ctx.unvalued.length} outputs confirmed` }],
            effort: 'Low',
            confidence: 'Medium',
            grounding: 'indicative',
            impact: timeShare(top.reduce((t, { o }) => t + (o.totalHours || 0), 0) / 2),
            subjects: [{ kind: 'unvalued', key: 'all' }],
        });
    }

    // priority = impact x confidence / effort, scaled to 100
    const raw = candidates.map((c) => (c.impact * CONFIDENCE_WEIGHT[c.confidence]) / EFFORT_WEIGHT[c.effort]);
    const max = Math.max(...raw, 0);
    const scored = candidates
        .map((c, i) => ({ c, priority: max > 0 ? Math.max(1, Math.round((100 * raw[i]) / max)) : 0 }))
        .sort((a, b) => b.priority - a.priority);

    // automation is gated by an assurance check that the outputs it touches keep their value
    const needsGate = scored.some((s) => s.c.enablesAutomation);
    const decisions: EpmDecision[] = [];
    if (needsGate) {
        const automated = scored.filter((s) => s.c.enablesAutomation).flatMap((s) => s.c.nodes.filter((n) => n.kind === 'action'));
        decisions.push({
            sequence: 0,
            gate: true,
            lever: 'investigate',
            title: 'Assure output value before automating',
            signal: 'The automation decisions below remove manual work. Confirm the outputs keep their value and that no judgement is lost.',
            rationale: 'Check each automated step still produces outputs of the same value, and keep an exceptions path for people where judgement matters. This gates the automation work rather than competing with it.',
            nodes: automated.slice(0, 3),
            impacts: [{ dimension: 'Assurance', movement: 'protects value' }],
            effort: 'Low',
            confidence: 'n/a',
            dependsOn: 'Runs first',
            priority: 100,
            grounding: 'indicative',
            subjects: [],
        });
    }
    scored.forEach(({ c, priority }, i) => {
        const { impact, enablesAutomation, ...decision } = c;
        void impact;
        decisions.push({
            ...decision,
            sequence: i + 1,
            gate: false,
            priority,
            dependsOn: enablesAutomation && needsGate ? 'Gate check' : 'None',
        });
    });
    return decisions;
};

// Whether a future state resolves the signals a current-state decision addresses: resolved when all of them are gone,
// improved when some are, and open when none are. A gate isn't judged, and a decision that addresses no specific signal
// (such as amplifying a high-value step) can't be checked against the model, so it's untracked.
export const decisionStatus = (decision: EpmDecision, current: EpmScorecard, future: EpmScorecard): EpmDecisionStatus | undefined => {
    if (decision.gate) return undefined;
    if (!decision.subjects.length) return 'untracked';
    const outcomes = decision.subjects.map((subject): EpmDecisionStatus => {
        if (subject.kind === 'unvalued') {
            const before = current.signals.unvalued.length;
            const after = future.signals.unvalued.length;
            return after === 0 ? 'resolved' : after < before ? 'improved' : 'open';
        }
        // a wait is resolved when items no longer wait there, and improved when they wait less
        if (subject.kind === 'waits' && current.waitRows && future.waitRows) {
            const before = current.waitRows.find((w) => w.key === subject.key)?.waitEach || 0;
            const after = future.waitRows.find((w) => w.key === subject.key)?.waitEach || 0;
            return after === 0 ? 'resolved' : after < before ? 'improved' : 'open';
        }
        // a kind of signal the scorecard doesn't track can't be checked, rather than stopping the page
        const remaining = future.signals[subject.kind];
        if (!remaining) return 'untracked';
        return remaining.includes(subject.key) ? 'open' : 'resolved';
    });
    if (outcomes.every((o) => o === 'untracked')) return 'untracked';
    if (outcomes.every((o) => o === 'resolved')) return 'resolved';
    if (outcomes.some((o) => o !== 'open')) return 'improved';
    return 'open';
};

// the decisions in each status, for the future state summary
export const countDecisionStatuses = (decisions: EpmDecision[], current: EpmScorecard, future: EpmScorecard) =>
    decisions.reduce(
        (counts, d) => {
            const status = decisionStatus(d, current, future);
            if (status) counts[status]++;
            return counts;
        },
        { resolved: 0, improved: 0, open: 0, untracked: 0 } as { [status in EpmDecisionStatus]: number },
    );

// Turns decisions generated by AI into scorecard decisions. The AI's output is checked against the evidence it was
// given: nodes must exist in the model, and each decision can only address the signals it was shown, which also lets
// the future state check whether a decision is resolved. Priority uses the same formula as the rule-based decisions.
export const decisionsFromAI = (generated: AIEpmDecision[], evidence: EpmDecisionEvidence): EpmDecision[] => {
    const known: { [kind in EpmNodeKind]: Set<string> } = {
        action: new Set(evidence.actions.map((a) => a.name)),
        role: new Set(evidence.roles.map((r) => r.name)),
        system: new Set(evidence.systems.map((s) => s.name)),
        info: new Set(evidence.outputs.map((o) => o.name)),
    };
    const signalIds = new Set(evidence.signals.map((s) => s.id));
    const levers = new Set<EpmLever>(['eliminate', 'automate', 'substitute', 'amplify', 'resequence', 'protect', 'leverage', 'investigate']);
    const level = (l: string): 'Low' | 'Medium' | 'High' => (l === 'Low' || l === 'High' ? l : 'Medium');

    const checked = generated
        .filter((d) => d.title && levers.has(d.lever))
        .slice(0, 8)
        .map((d) => {
            // every signal the decision addresses, so a future state can resolve it fully or in part
            const subjects = [...new Set((d.addresses || []).filter((a) => signalIds.has(a)))].map((address) => {
                const split = address.indexOf(':');
                return { kind: address.substring(0, split), key: address.substring(split + 1) } as EpmSubject;
            });
            const impact = Math.min(10, Math.max(1, Math.round(d.impact || 1)));
            const effort = level(d.effort);
            const confidence = level(d.confidence);
            return {
                d,
                effort,
                confidence,
                raw: d.gate ? 0 : (impact * CONFIDENCE_WEIGHT[confidence]) / EFFORT_WEIGHT[effort],
                nodes: (d.nodes || []).filter((n) => known[n.kind]?.has(n.name)),
                subjects,
            };
        });

    // gates first, then by priority, then moved so that a decision comes after the one it depends on
    const max = Math.max(...checked.map((c) => c.raw), 0);
    const gates = checked.filter((c) => c.d.gate);
    const ranked = checked.filter((c) => !c.d.gate).sort((a, b) => b.raw - a.raw);
    const ordered: typeof ranked = [];
    const place = (c: (typeof ranked)[number], seen: Set<string>) => {
        if (ordered.includes(c) || seen.has(c.d.title)) return;
        seen.add(c.d.title);
        const dependency = ranked.find((r) => r !== c && r.d.title === c.d.dependsOn);
        if (dependency) place(dependency, seen);
        ordered.push(c);
    };
    ranked.forEach((c) => place(c, new Set()));

    const all = [...gates, ...ordered];
    const gateBadge = (i: number) => (gates.length > 1 ? `G${i + 1}` : 'G');
    const sequenceOf = (title: string) => {
        const i = all.findIndex((c) => c.d.title === title);
        if (i < 0) return undefined;
        return all[i].d.gate ? `Gate ${gates.length > 1 ? gateBadge(i) : 'check'}` : `Decision ${i - gates.length + 1}`;
    };
    return all.map((c, i) => ({
        sequence: c.d.gate ? 0 : i - gates.length + 1,
        gate: c.d.gate,
        lever: c.d.lever,
        title: c.d.title,
        signal: c.d.signal,
        rationale: c.d.rationale,
        nodes: c.nodes,
        impacts: (c.d.impacts || []).slice(0, 4),
        effort: c.effort,
        confidence: c.d.gate ? 'n/a' : c.confidence,
        dependsOn: c.d.gate ? 'Runs first' : (c.d.dependsOn && sequenceOf(c.d.dependsOn)) || 'None',
        priority: c.d.gate ? 100 : max > 0 ? Math.max(1, Math.round((100 * c.raw) / max)) : 0,
        grounding: c.d.grounded ? 'grounded' : 'indicative',
        subjects: c.subjects,
        badge: c.d.gate ? gateBadge(i) : undefined,
    }));
};

const median = (values: number[]) => {
    if (!values.length) return 0;
    const sorted = values.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const joinList = (items: string[]) =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// a time in hours as it reads best: minutes under an hour, days (of 24 hours, as durations are entered) from a day
export const elapsed = (hours: number) => {
    const round = (v: number) => (v < 10 ? Math.round(v * 10) / 10 : Math.round(v));
    if (!(hours > 0)) return '0 mins';
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} mins`;
    if (hours < 24) return `${round(hours)} ${round(hours) === 1 ? 'hr' : 'hrs'}`;
    const days = round(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
};

const round2 = (v: number) => Math.round(v * 100) / 100;
